import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import VideoRoom from './VideoRoom';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Camera, 
  CameraOff, 
  Mic, 
  MicOff, 
  Volume2,
  VolumeX,
  MessageCircle,
  Shield,
  UserX,
  Flag,
  HelpCircle,
  X,
  Shuffle,
  ChevronRight,
  Home,
  PhoneOff,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Heart
} from 'lucide-react';

const SpeedDatingDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Core states
  const [showCountdown, setShowCountdown] = useState(true);
  const [countdownNumber, setCountdownNumber] = useState(5);
  const [sessionReady, setSessionReady] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const [timerInitialized, setTimerInitialized] = useState(false); // Track if timer loaded from backend
  const timerIntervalRef = useRef(null); // Store timer interval reference
  const timerEndedRef = useRef(false); // Track if timer has ended to prevent multiple triggers
  
  // Icebreaker states
  const [currentIcebreaker, setCurrentIcebreaker] = useState(0);
  const [expandedIcebreaker, setExpandedIcebreaker] = useState(null);
  const [showMobileBubbleExpanded, setShowMobileBubbleExpanded] = useState(false);
  
  // Safety states
  const [showSafetyMenu, setShowSafetyMenu] = useState(false);
  const [showEndSessionConfirm, setShowEndSessionConfirm] = useState(false);
  const [showBlockUserConfirm, setShowBlockUserConfirm] = useState(false);
  const [showReportUser, setShowReportUser] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [showSafetyTips, setShowSafetyTips] = useState(false);
  
  // Enhanced safety states for new features
  const [showSafetyOnboarding, setShowSafetyOnboarding] = useState(false);
  const [safetyOnboardingDismissed, setSafetyOnboardingDismissed] = useState(false);
  const [safetyRemindersEnabled, setSafetyRemindersEnabled] = useState(true);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showSafetyReminder, setShowSafetyReminder] = useState(false);
  const [safetyStatus, setSafetyStatus] = useState('safe'); // safe, caution, alert
  const [timerPaused, setTimerPaused] = useState(false); // Pause timer during safety onboarding
  const [partnerConnected, setPartnerConnected] = useState(false); // Track if partner has joined
  
  // Upgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showVipSessionComplete, setShowVipSessionComplete] = useState(false);
  const [userTier, setUserTier] = useState('free'); // free, premium, vip
  
  // Queue states
  const [queueData, setQueueData] = useState(null);
  const [showQueueModal, setShowQueueModal] = useState(false);
  
  // Rating states
  const [showRatingScreen, setShowRatingScreen] = useState(false);
  const [showMemorableMomentModal, setShowMemorableMomentModal] = useState(false);
  const [memorableMoment, setMemorableMoment] = useState('');
  const [pendingRating, setPendingRating] = useState(null);
  const [userRating, setUserRating] = useState(null); // 'like' or 'pass'
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  
  // Session-specific notification states
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionState, setSessionState] = useState('loading'); // loading, queuing, in_session, ended, error
  const [partner, setPartner] = useState(null);
  
  // Video room states
  const [roomName, setRoomName] = useState('');
  const [userIdentity, setUserIdentity] = useState('');
  const [videoReady, setVideoReady] = useState(false);
  
  // Debug state changes
  useEffect(() => {
    console.log('🎯 showVipSessionComplete state changed to:', showVipSessionComplete);
  }, [showVipSessionComplete]);
  
  useEffect(() => {
    console.log('🎯 userTier changed to:', userTier);
  }, [userTier]);
  
  // UI states
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [partnerAudioEnabled, setPartnerAudioEnabled] = useState(true);
  
  // Video functionality
  const [localStream, setLocalStream] = useState(null);
  const [videoInitialized, setVideoInitialized] = useState(false);
  const [partnerStream, setPartnerStream] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const partnerVideoRef = useRef(null);
  const twilioRoomRef = useRef(null);
  // Twilio video state
  const [twilioToken, setTwilioToken] = useState(null);
  const [twilioRoomName, setTwilioRoomName] = useState(null);
  const [useRealVideo, setUseRealVideo] = useState(false);
  const [twilioError, setTwilioError] = useState(null);

  // Real user profile data
  const [currentProfileData, setCurrentProfileData] = useState(null);
  const [matchedUser, setMatchedUser] = useState(null);
  const [currentMatchId, setCurrentMatchId] = useState(null); // Store match_id in state
  
  // Custom setter that logs every time matchedUser changes
  const setMatchedUserWithLog = (data) => {
    console.log('🎭 setMatchedUser called with:', data);
    console.trace('📍 Call stack:');
    setMatchedUser(data);
  };
  const [icebreakers, setIcebreakers] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);

  // Countdown effect
  useEffect(() => {
    if (showCountdown && countdownNumber > 0) {
      const timer = setTimeout(() => {
        if (countdownNumber === 1) {
          console.log('🎯 COUNTDOWN FINISHED! Hiding countdown (session will start after data loads)...');
          setShowCountdown(false);
          // DON'T set sessionReady here - let startSpeedDateWithMatch() do it after loading timer
          
          // Scroll to top when session starts
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth'
            });
            console.log('📍 Scrolled to top after countdown completion');
          }, 100);
        } else {
          console.log(`⏰ Countdown: ${countdownNumber} → ${countdownNumber - 1}`);
          setCountdownNumber(countdownNumber - 1);
        }
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [countdownNumber, showCountdown]);

  // Timer effect - paused during safety onboarding AND until partner connects
  useEffect(() => {
    console.log('🎯 Timer useEffect triggered - sessionReady:', sessionReady, 'timeLeft:', timeLeft, 'timerPaused:', timerPaused, 'partnerConnected:', partnerConnected, 'timerInitialized:', timerInitialized);
    
    // Clear any existing timer
    if (timerIntervalRef.current) {
      console.log('🧹 Clearing existing timer interval');
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Reset timer ended flag when conditions change
    if (!sessionReady || timerPaused) {
      timerEndedRef.current = false;
    }
    
    // 🔥 TIMER SYNC FIX: Timer starts when BOTH users connected OR if timer is finishing (< 10 sec)
    // CRITICAL FIX: Allow timer to run if under 10 seconds even if partner disconnects
    const shouldRunTimer = sessionReady && !timerPaused && !timerEndedRef.current && (partnerConnected || timeLeft < 10);
    
    if (shouldRunTimer) {
      console.log('✅ Starting timer interval from:', timeLeft, 'seconds (partnerConnected:', partnerConnected, ')');
      
      timerIntervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          console.log('⏱️ Timer tick - current:', prev);
          
          // Already at 0 or ended
          if (prev <= 0 || timerEndedRef.current) {
            console.log('⏰ Timer already at 0 or ended, stopping');
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            return 0;
          }
          
          // When reaching 1, trigger end sequence ONCE
          if (prev === 1 && !timerEndedRef.current) {
            timerEndedRef.current = true; // Prevent multiple triggers
            
            console.log('⏰ Timer reached 1 second - ending now!');
            console.log('🛑 Stopping camera and microphone due to timer expiration');
            
            // Clear interval immediately
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            // CRITICAL: Stop local camera stream IMMEDIATELY
            if (localStream) {
              console.log('🛑 Stopping camera immediately due to timer expiration');
              localStream.getTracks().forEach(track => {
                track.stop();
                console.log(`🛑 Stopped ${track.kind} track`);
              });
              setLocalStream(null);
              setVideoInitialized(false);
            }
            
            // PROPER FIX: Use handleEndSession to ensure proper cleanup
            // This ensures consistent cleanup whether timer expires or manual end
            console.log('⏰ Timer expired - calling handleEndSession for proper cleanup');
            
            // Call handleEndSession asynchronously but don't block
            handleEndSession().catch(error => {
              console.error('❌ Timer expiration cleanup failed:', error);
              // Even if cleanup fails, continue to rating screen
              // Passive cleanup will handle orphaned session after 90s
            });
            
            // Show rating screen immediately (don't wait for cleanup)
            console.log('⭐ Timer expired - showing rating screen');
            setShowRatingScreen(true);
            
            return 0;
          }
          
          // Normal countdown
          return prev - 1;
        });
      }, 1000);
      
      return () => {
        console.log('🧹 Cleaning up timer interval on unmount');
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      };
    } else {
      console.log('❌ Timer NOT starting - sessionReady:', sessionReady, '!timerPaused:', !timerPaused, 'partnerConnected:', partnerConnected, 'timerInitialized:', timerInitialized, 'timerEnded:', timerEndedRef.current, 'timeLeft:', timeLeft);
      
      // CRITICAL FIX: If timer is not running but should continue (time < 10 and partner disconnected)
      // Clean up any existing interval to allow restart
      if (timerIntervalRef.current && !partnerConnected && timeLeft < 10 && !timerEndedRef.current) {
        console.log('🔄 Cleaning up interval to allow timer restart despite partner disconnect');
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [sessionReady, timerPaused, partnerConnected, timerInitialized, localStream, currentSession, timeLeft]);

  // Scroll to top when session becomes ready
  useEffect(() => {
    if (sessionReady) {
      // Ensure page scrolls to top when session starts
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      console.log('📍 Session ready - scrolled to top');
      
      // Pause timer and show safety onboarding immediately
      setTimerPaused(true);
      setShowSafetyOnboarding(true);
      console.log('⏸️ Timer paused for safety onboarding');
    }
  }, [sessionReady]);

  // Start timer when partner connects (if safety modal already dismissed)
  useEffect(() => {
    if (partnerConnected && !showSafetyOnboarding && timerPaused && sessionReady) {
      console.log('🎬 Partner joined and safety modal dismissed - starting timer!');
      setTimerPaused(false);
      setSessionStartTime(Date.now());
    }
  }, [partnerConnected, showSafetyOnboarding, timerPaused, sessionReady]);

  // Periodic safety reminders
  useEffect(() => {
    if (sessionReady && safetyRemindersEnabled && sessionStartTime) {
      const reminderInterval = setInterval(() => {
        const elapsed = (Date.now() - sessionStartTime) / 1000;
        
        // Show reminders at 60s, 120s intervals
        if (elapsed >= 60 && elapsed < 65) {
          setShowSafetyReminder(true);
          setTimeout(() => setShowSafetyReminder(false), 5000);
        } else if (elapsed >= 90 && elapsed < 95) {
          // Check-in prompt at 90 seconds
          setShowSafetyReminder(true);
          setTimeout(() => setShowSafetyReminder(false), 8000);
        }
      }, 1000);

      return () => clearInterval(reminderInterval);
    }
  }, [sessionReady, safetyRemindersEnabled, sessionStartTime]);

  // ESC key emergency exit
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && sessionReady) {
        console.log('🚨 Emergency exit via ESC key');
        handleEndSession().catch(error => {
          console.error('❌ ESC key cleanup failed:', error);
        });
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [sessionReady]);

  // Initialize real speed dating session
  useEffect(() => {
    const initialize = async () => {
      console.log('🚀 Component mounted - checking for partner info');
      
      // CRITICAL DEBUG: Call backend to log this initialization
      try {
        const token = localStorage.getItem('token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/debug/log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            message: 'SpeedDatingDemo initialized',
            url_params: window.location.search,
            has_partner_info: !!location.state?.partnerInfo,
            has_partner_param: !!new URLSearchParams(window.location.search).get('partner')
          })
        }).catch(() => {}); // Ignore errors
      } catch (e) {}
      
      // FIRST PRIORITY: Check if partner info was passed from navigation state (e.g., from messages page)
      if (location.state?.partnerInfo) {
        const { id, name, photo_url } = location.state.partnerInfo;
        console.log('✅ Partner info from navigation state:', { id, name, photo_url });
        await startSpeedDateWithMatch(id);
        return; // EXIT - partner selected from messages
      }
      
      // SECOND PRIORITY: Check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const partnerId = urlParams.get('partner');
      
      if (partnerId) {
        console.log('✅ Partner parameter found:', partnerId, '- Using selected match ONLY');
        await startSpeedDateWithMatch(partnerId);
        return; // EXIT - do not run any other initialization
      }
      
      // No partner specified - continue with normal initialization (random match)
      console.log('ℹ️ No partner specified - running normal initialization');
      await initializeSpeedDatingSession();
    };
    
    initialize();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const nextIcebreaker = () => {
    setCurrentIcebreaker((prev) => (prev + 1) % icebreakers.length);
  };

  const shuffleIcebreakers = () => {
    setCurrentIcebreaker(Math.floor(Math.random() * icebreakers.length));
  };

  // Real user profile and session management
  const fetchCurrentUser = async () => {
    try {
      console.log('🔄 Starting fetchCurrentUser...');
      const token = localStorage.getItem('token');
      const isDemo = window.location.pathname === '/speed-dating-demo';
      
      if (!token && !isDemo) {
        console.log('❌ No token found, redirecting to login');
        navigate('/login');
        return;
      }
      
      if (isDemo) {
        console.log('🎭 Demo mode - skipping token authentication');
        return; // Skip API calls in demo mode
      }

      console.log('📞 Making API call to /api/me...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();
        console.log('✅ User data fetched successfully:', userData);
        setCurrentProfileData(userData);
        
        // Fetch subscription status to determine user tier
        console.log('🔄 Fetching subscription status...');
        await fetchSubscriptionStatus(token);
      } else {
        console.log('❌ API response not ok:', response.status, response.statusText);
        throw new Error('Failed to fetch user profile');
      }
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
      setProfileError('Failed to load your profile');
    }
  };

  const fetchSubscriptionStatus = async (token) => {
    try {
      console.log('📞 Making API call to /api/subscription/status...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const subscriptionData = await response.json();
        console.log('✅ Subscription data fetched:', subscriptionData);
        const tier = subscriptionData.subscription?.tier || 'free';
        console.log('🎯 Setting user subscription tier to:', tier);
        setUserTier(tier);
      } else {
        console.log('⚠️ No subscription found, response:', response.status, 'defaulting to free tier');
        setUserTier('free');
      }
    } catch (error) {
      console.error('❌ Error fetching subscription status:', error);
      setUserTier('free'); // Default to free on error
    }
  };

  const findSpeedDateMatch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/find-match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const matchData = await response.json();
        console.log('⚠️ findSpeedDateMatch() is overwriting matchedUser with random match!');
        setMatchedUserWithLog(matchData.matched_user);
        return matchData;
      } else {
        throw new Error('Failed to find speed dating match');
      }
    } catch (error) {
      console.error('Error finding speed dating match:', error);
      setProfileError('Failed to find a match. Please try again.');
    }
  };

  const fetchPersonalizedIcebreakers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/icebreakers/personalized`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🎯 Icebreakers received:', data.icebreakers?.length || 0, 'questions');
        console.log('💎 Premium access:', data.has_premium_access);
        console.log('✨ Personalized:', data.personalized);
        setIcebreakers(data.icebreakers || []);
      } else {
        // Fallback to default icebreakers if API fails
        console.warn('⚠️ Icebreaker API failed, using fallback');
        setIcebreakers([
          "What's the most adventurous thing you've done recently?",
          "If you could have dinner with anyone, who would it be?",
          "What's your favorite way to spend a weekend?",
          "What's something you're passionate about?",
          "If you could travel anywhere right now, where would you go?",
          "What's your ideal way to spend a Friday night?",
          "What's the best piece of advice you've ever received?",
          "What accomplishment are you most proud of, and why does it matter to you?"
        ]);
      }
    } catch (error) {
      console.error('Error fetching personalized icebreakers:', error);
      // Use fallback icebreakers
      setIcebreakers([
        "What's the most adventurous thing you've done recently?",
        "If you could have dinner with anyone, who would it be?",
        "What's your favorite way to spend a weekend?",
        "What's something you're passionate about?",
        "If you could travel anywhere right now, where would you go?",
        "What's your ideal way to spend a Friday night?",
        "What's the best piece of advice you've ever received?",
        "What accomplishment are you most proud of, and why does it matter to you?"
      ]);
    }
  };

  // Video functionality
  // Request wake lock for mobile devices
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        const lock = await navigator.wakeLock.request('screen');
        console.log('🔒 Wake lock acquired - screen will stay awake');
        
        lock.addEventListener('release', () => {
          console.log('🔓 Wake lock released');
        });
        
        return lock;
      } else {
        console.log('⚠️ Wake Lock API not supported');
        return null;
      }
    } catch (err) {
      console.error('❌ Wake lock error:', err);
      return null;
    }
  };
  
  const initializeCamera = async () => {
    try {
      console.log('🎥 Initializing camera...');
      console.log('📱 Navigator.mediaDevices available:', !!navigator.mediaDevices);
      console.log('📱 getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);
      
      // Request wake lock for mobile
      await requestWakeLock();
      
      // Request camera with mobile-friendly constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user' // Front camera on mobile
        },
        audio: true
      });
      
      console.log('✅ Camera stream obtained successfully!');
      console.log('📊 Stream ID:', stream.id);
      console.log('📊 Stream active:', stream.active);
      console.log('📊 Video tracks:', stream.getVideoTracks().length);
      console.log('📊 Audio tracks:', stream.getAudioTracks().length);
      
      if (stream.getVideoTracks().length > 0) {
        const videoTrack = stream.getVideoTracks()[0];
        console.log('📹 Video track label:', videoTrack.label);
        console.log('📹 Video track enabled:', videoTrack.enabled);
        console.log('📹 Video track muted:', videoTrack.muted);
        console.log('📹 Video track settings:', videoTrack.getSettings());
      }
      
      console.log('💾 Setting localStream state...');
      setLocalStream(stream);
      
      console.log('✅ localStream state set, video element should update');
      
      // Set as initialized immediately - let video element handle playback
      setVideoInitialized(true);
      
      console.log('✅ videoInitialized set to true');
      
      // Immediate assignment with detailed logging
      const assignVideoStreamNow = () => {
        if (localVideoRef.current && stream) {
          console.log('📺 Assigning video stream to element...');
          console.log('📺 Video element exists:', !!localVideoRef.current);
          console.log('📺 Video element readyState:', localVideoRef.current.readyState);
          console.log('📺 Stream exists:', !!stream);
          console.log('📺 Stream active:', stream.active);
          
          // Force assignment
          localVideoRef.current.srcObject = stream;
          localVideoRef.current.muted = true; // Ensure muted for autoplay
          
          console.log('📺 srcObject assigned:', localVideoRef.current.srcObject === stream);
          console.log('✅ Video stream assigned - video element should display it automatically');
          
          // Try to play, but don't block on it
          const attemptPlay = async () => {
            try {
              await localVideoRef.current.play();
              console.log('✅✅✅ Video playing successfully! ✅✅✅');
              console.log('📺 Video dimensions:', localVideoRef.current.videoWidth, 'x', localVideoRef.current.videoHeight);
              console.log('📺 Video paused:', localVideoRef.current.paused);
              console.log('📺 Video currentTime:', localVideoRef.current.currentTime);
              
              // Check if video actually has dimensions
              if (localVideoRef.current.videoWidth === 0 || localVideoRef.current.videoHeight === 0) {
                console.warn('⚠️ Video has no dimensions! This might cause black screen');
                console.log('💡 Waiting for metadata...');
              }
            } catch (playError) {
              console.log('ℹ️ Autoplay blocked or failed:', playError.name);
              console.log('💡 Video stream is assigned, it should still display');
              console.log('💡 If you see a black screen:');
              console.log('   1. Check if camera permission was granted');
              console.log('   2. Try clicking the camera button to retry');
              console.log('   3. Check video element in DevTools');
            }
          };
          
          // Try to play after a small delay
          setTimeout(attemptPlay, 100);
          
          // Also log after a longer delay to see final state
          setTimeout(() => {
            console.log('🔍 Final check after 2 seconds:');
            console.log('  - Video element exists:', !!localVideoRef.current);
            console.log('  - srcObject:', !!localVideoRef.current?.srcObject);
            console.log('  - Video dimensions:', localVideoRef.current?.videoWidth, 'x', localVideoRef.current?.videoHeight);
            console.log('  - Video paused:', localVideoRef.current?.paused);
            console.log('  - readyState:', localVideoRef.current?.readyState);
          }, 2000);
        } else {
          console.error('❌ Cannot assign stream - missing video ref or stream');
          console.error('  - localVideoRef.current:', !!localVideoRef.current);
          console.error('  - stream:', !!stream);
        }
      };
      
      // Wait a moment for React to update, then assign stream
      setTimeout(() => {
        console.log('⏰ Timeout reached, assigning stream to video element...');
        assignVideoStreamNow();
      }, 100);
      
      // Stream assignment is handled by the setTimeout above
      console.log('🎬 Camera initialization complete, waiting for video element to render');
      
    } catch (error) {
      console.error('❌ Camera initialization failed:', error.name, '-', error.message);
      console.error('📱 This might be due to:');
      console.error('  1. Camera permission denied');
      console.error('  2. No camera available on device');
      console.error('  3. Camera in use by another app');
      console.error('💡 Try clicking the camera button to retry');
      setVideoInitialized(false);
      setLocalStream(null);
    }
  };

  const toggleCamera = async () => {
    console.log('🔘 Camera button clicked, current state:', cameraEnabled);
    console.log('🔍 Twilio room status:', twilioRoomRef.current ? 'CONNECTED' : 'NOT CONNECTED');
    
    if (!twilioRoomRef.current) {
      console.warn('⚠️ Twilio room not connected - camera will be controlled locally only');
      // Still toggle local stream if available
      if (localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !cameraEnabled;
          setCameraEnabled(!cameraEnabled);
          console.log(`📹 Local camera ${!cameraEnabled ? 'enabled' : 'disabled'} (no Twilio connection)`);
        }
      }
      return;
    }
    
    const localParticipant = twilioRoomRef.current.localParticipant;
    console.log('👤 Local participant has', localParticipant.videoTracks.size, 'video track(s)');
    
    if (cameraEnabled) {
      // TURN OFF: Stop and unpublish the video track
      console.log('📹 Turning camera OFF - stopping transmission to partner');
      
      localParticipant.videoTracks.forEach(publication => {
        if (publication.track) {
          console.log('🎯 Stopping video track:', publication.track.name);
          // Stop the track (turns off camera)
          publication.track.stop();
          // Unpublish from room (stops transmission to partner)
          localParticipant.unpublishTrack(publication.track);
          console.log('🚫 Video track STOPPED and UNPUBLISHED - partner CANNOT see you now');
        }
      });
      
      setCameraEnabled(false);
      console.log('✅ Camera is now OFF - Privacy protected');
      
    } else {
      // TURN ON: Create new track and publish
      console.log('📹 Turning camera ON - starting transmission to partner');
      
      try {
        // PROPER FIX: Use Twilio's createLocalVideoTrack method
        const Video = (await import('twilio-video')).default;
        
        // Validate localParticipant state
        if (!localParticipant || localParticipant.state === 'disconnected') {
          throw new Error('Local participant not in valid state');
        }
        
        console.log('✅ Local participant state valid:', localParticipant.state);
        
        // Use Twilio's recommended method to create video track
        const localVideoTrack = await Video.createLocalVideoTrack({
          width: 640,
          height: 480
        });
        
        console.log('📹 Twilio LocalVideoTrack created successfully');
        
        // Publish track to room with error handling
        const publication = await localParticipant.publishTrack(localVideoTrack);
        
        console.log('✅ Track published successfully:', publication.trackSid);
        
        // Now update local preview
        const newStream = new MediaStream([localVideoTrack.mediaStreamTrack]);
        
        // Add existing audio track if available
        if (localStream) {
          const audioTrack = localStream.getAudioTracks()[0];
          if (audioTrack) {
            newStream.addTrack(audioTrack);
            console.log('🎤 Preserved existing audio track');
          }
        }
        
        // Update React state
        setLocalStream(newStream);
        
        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = newStream;
          await localVideoRef.current.play();
          console.log('📹 Local video preview updated');
        }
        
        console.log('✅ Camera ON complete - partner CAN see you now');
        setCameraEnabled(true);
        
      } catch (error) {
        console.error('❌ Error during camera restart:', error);
        console.error('Error details:', error.message, error.code);
        
        // Wait briefly and check if camera actually turned on despite the error
        setTimeout(() => {
          const hasVideoTrack = localParticipant.videoTracks.size > 0;
          const hasLocalStream = localStream && localStream.getVideoTracks().length > 0;
          
          if (hasVideoTrack || hasLocalStream) {
            console.log('✅ Camera actually ON despite error - suppressing false alert');
            setCameraEnabled(true);
          } else {
            // Camera genuinely failed - show appropriate error
            console.error('❌ Camera genuinely failed to turn on');
            
            if (error.message.includes('participant')) {
              alert('Camera toggle failed: Connection issue. Please refresh the page.');
            } else if (error.name === 'NotAllowedError') {
              alert('Camera access denied. Please allow camera access and try again.');
            } else {
              alert('Failed to turn camera back on. Error: ' + error.message);
            }
            
            setCameraEnabled(false);
          }
        }, 500);
      }
    }
  };

  const toggleAudio = async () => {
    console.log('🔘 Mic button clicked, current state:', audioEnabled);
    console.log('🔍 Twilio room status:', twilioRoomRef.current ? 'CONNECTED' : 'NOT CONNECTED');
    
    if (!twilioRoomRef.current) {
      console.warn('⚠️ Twilio room not connected - microphone will be controlled locally only');
      // Still toggle local stream if available
      if (localStream) {
        const audioTrack = localStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !audioEnabled;
          setAudioEnabled(!audioEnabled);
          console.log(`🎤 Local microphone ${!audioEnabled ? 'enabled' : 'disabled'} (no Twilio connection)`);
        }
      }
      return;
    }
    
    const localParticipant = twilioRoomRef.current.localParticipant;
    console.log('👤 Local participant has', localParticipant.audioTracks.size, 'audio track(s)');
    
    if (audioEnabled) {
      // TURN OFF: Stop and unpublish the audio track
      console.log('🎤 Turning microphone OFF - stopping transmission to partner');
      
      localParticipant.audioTracks.forEach(publication => {
        if (publication.track) {
          console.log('🎯 Stopping audio track:', publication.track.name);
          // Stop the track (turns off microphone)
          publication.track.stop();
          // Unpublish from room (stops transmission to partner)
          localParticipant.unpublishTrack(publication.track);
          console.log('🚫 Audio track STOPPED and UNPUBLISHED - partner CANNOT hear you now');
        }
      });
      
      setAudioEnabled(false);
      console.log('✅ Microphone is now OFF - Privacy protected');
      
    } else {
      // TURN ON: Create new track and publish
      console.log('🎤 Turning microphone ON - starting transmission to partner');
      
      try {
        // PROPER FIX: Use Twilio's createLocalAudioTrack method
        const Video = (await import('twilio-video')).default;
        
        // Validate localParticipant state
        if (!localParticipant || localParticipant.state === 'disconnected') {
          throw new Error('Local participant not in valid state');
        }
        
        console.log('✅ Local participant state valid:', localParticipant.state);
        
        // Use Twilio's recommended method to create audio track
        const localAudioTrack = await Video.createLocalAudioTrack();
        
        console.log('🎤 Twilio LocalAudioTrack created successfully');
        
        // Publish track to room with error handling
        const publication = await localParticipant.publishTrack(localAudioTrack);
        
        console.log('✅ Track published successfully:', publication.trackSid);
        
        // Now update local stream
        const newStream = new MediaStream([localAudioTrack.mediaStreamTrack]);
        
        // Add existing video track if available
        if (localStream) {
          const videoTrack = localStream.getVideoTracks()[0];
          if (videoTrack) {
            newStream.addTrack(videoTrack);
            console.log('📹 Preserved existing video track');
          }
        }
        
        // Update React state
        setLocalStream(newStream);
        
        // Update local video element (for audio)
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = newStream;
          console.log('📹 Local preview updated with audio');
        }
        
        console.log('✅ Microphone ON complete - partner CAN hear you now');
        setAudioEnabled(true);
        
      } catch (error) {
        console.error('❌ Failed to restart microphone:', error);
        console.error('Error details:', error.message, error.code);
        
        // More helpful error message
        if (error.message.includes('participant')) {
          alert('Microphone toggle failed: Connection issue. Please refresh the page.');
        } else if (error.name === 'NotAllowedError') {
          alert('Microphone access denied. Please allow microphone access and try again.');
        } else {
          alert('Failed to turn microphone back on. Error: ' + error.message);
        }
        
        // Try to restore state
        setAudioEnabled(false);
      }
    }
  };

  const togglePartnerAudio = () => {
    console.log('🔘 Speaker button clicked, current state:', partnerAudioEnabled);
    
    const newState = !partnerAudioEnabled;
    
    // Mute/unmute all remote audio elements (partner's voice)
    const audioElements = document.querySelectorAll('audio[data-participant]');
    console.log('🔊 Found', audioElements.length, 'remote audio elements');
    
    audioElements.forEach(audio => {
      audio.muted = !newState; // If newState is true (enabled), unmute. If false (disabled), mute.
      console.log('🔊 Audio element muted:', audio.muted);
    });
    
    // Also control remote audio via Twilio if available
    if (twilioRoomRef.current) {
      twilioRoomRef.current.participants.forEach(participant => {
        participant.audioTracks.forEach(publication => {
          if (publication.track) {
            if (newState) {
              publication.track.attach().forEach(el => el.muted = false);
              console.log('✅ Partner audio ENABLED for participant:', participant.identity);
            } else {
              publication.track.attach().forEach(el => el.muted = true);
              console.log('🚫 Partner audio DISABLED for participant:', participant.identity);
            }
          }
        });
      });
    }
    
    // Also mute the partner video element if it has audio
    const partnerVideo = partnerVideoRef.current?.querySelector('video');
    if (partnerVideo) {
      partnerVideo.muted = !newState;
      console.log('🔊 Partner video element muted:', partnerVideo.muted);
    }
    
    setPartnerAudioEnabled(newState);
    console.log('🔊 Speaker toggle complete. New state:', newState);
  };

  const stopCamera = () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
      setVideoInitialized(false);
      console.log('🛑 Camera stopped');
    }
  };

  const cleanupTwilioConnection = async () => {
    try {
      console.log('🧹 Cleaning up Twilio connection...');
      
      // Disconnect from Twilio room
      if (twilioRoomRef.current) {
        console.log('🔌 Disconnecting from Twilio room');
        
        // Remove all event listeners before disconnecting
        twilioRoomRef.current.removeAllListeners();
        
        // Disconnect all participants
        twilioRoomRef.current.participants.forEach(participant => {
          console.log('👋 Removing participant:', participant.identity);
          participant.removeAllListeners();
        });
        
        // Disconnect the room
        twilioRoomRef.current.disconnect();
        twilioRoomRef.current = null;
        console.log('✅ Disconnected from Twilio room');
      }
      
      // Clean up partner video stream
      if (partnerStream) {
        console.log('🛑 Stopping partner video stream');
        setPartnerStream(null);
      }
      
      // Clear partner video element
      if (partnerVideoRef.current) {
        partnerVideoRef.current.innerHTML = '';
        console.log('🧹 Cleared partner video element');
      }
      
      // Reset partner connection state
      setPartnerConnected(false);
      
      // Reset Twilio states
      setTwilioToken(null);
      setTwilioRoomName(null);
      setUseRealVideo(false);
      setTwilioError(null);
      
      console.log('✅ Twilio connection cleanup complete');
    } catch (error) {
      console.error('❌ Error during Twilio cleanup:', error);
    }
  };
  
  // Get Twilio video token
  const getTwilioToken = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Get current user's UUID from backend to match partner's UUID format
      const meResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000 // 10 second timeout
        }
      );
      
      const myUserId = meResponse.data.id;
      
      // Create consistent room name - sort IDs alphabetically so both users generate the same name
      // Use currentMatchId as unique identifier that both users share
      const ids = [myUserId, sessionId].sort();
      const uniqueId = currentMatchId || `${ids[0]}-${ids[1]}-${Date.now()}`;
      const roomName = `speed-date-${uniqueId}`;
      
      console.log('🎥 Requesting Twilio token for room:', roomName);
      console.log('🆔 Using match ID for room uniqueness:', currentMatchId);
      
      // DEBUG: Log to backend that video token is being requested
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/debug/log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            message: 'Requesting video token',
            room_name: roomName,
            match_id: currentMatchId,
            session_id: sessionId
          })
        }).catch(() => {});
      } catch (e) {}
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/video/token`,
        {
          user_id: myUserId,
          room_name: roomName,
          session_duration: 300
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );
      
      console.log('✅ Twilio token received, room:', response.data.room_name);
      
      setTwilioToken(response.data.token);
      setTwilioRoomName(response.data.room_name);
      setUseRealVideo(true);
      
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('❌ Twilio token error:', errorMsg);
      setTwilioError(errorMsg);
      setUseRealVideo(false);
      return null;
    }
  };

  // Initialize camera when component mounts or session starts AND safety modal is dismissed
  useEffect(() => {
    if (sessionReady && !localStream && safetyOnboardingDismissed) {
      console.log('🎯 Session ready, safety dismissed, and no stream - initializing camera');
      initializeCamera();
    }
  }, [sessionReady, safetyOnboardingDismissed]);
  
  // CRITICAL: Cleanup camera and connections when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting - cleaning up camera and connections');
      
      // Stop camera
      if (localStream) {
        console.log('🛑 Stopping camera on unmount');
        localStream.getTracks().forEach(track => {
          track.stop();
          console.log(`🛑 Stopped track: ${track.kind}`);
        });
        setLocalStream(null);
        setVideoInitialized(false);
      }
      
      // Cleanup Twilio connection
      if (twilioRoomRef.current) {
        console.log('🔌 Disconnecting Twilio on unmount');
        twilioRoomRef.current.disconnect();
        twilioRoomRef.current = null;
      }
      
      console.log('✅ Component cleanup complete');
    };
  }, []); // Empty deps = runs only on mount/unmount
  
  // Connect to Twilio and handle partner video
  useEffect(() => {
    const startBackendTimer = async () => {
      try {
        const token = localStorage.getItem('token');
        const matchId = matchedUser?.match_id || partner?.match_id;
        
        if (!matchId) {
          console.warn('⚠️ Cannot start timer - no match_id available');
          return;
        }
        
        console.log('⏱️ Starting backend timer for match:', matchId);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/start-timer?match_id=${matchId}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Backend timer started:', data);
        } else {
          console.warn('⚠️ Failed to start backend timer:', response.status);
        }
      } catch (error) {
        console.error('❌ Error starting backend timer:', error);
      }
    };
    
    const connectToTwilioRoom = async () => {
      // GUARD: Prevent duplicate connections
      if (twilioRoomRef.current) {
        console.log('⏭️ Already connected to Twilio room, skipping...');
        return;
      }
      
      if (sessionReady && twilioRoomName && twilioToken && localStream) {
        try {
          console.log('🔗 Connecting to Twilio room:', twilioRoomName);
          console.log('📹 Using existing local stream for Twilio connection');
          console.log('📱 Device info:', {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            isMobile: /Mobi|Android/i.test(navigator.userAgent)
          });
          
          // Import Twilio Video
          const Video = (await import('twilio-video')).default;
          console.log('✅ Twilio Video SDK loaded');
          
          // Get video and audio tracks from existing stream
          const videoTrack = localStream.getVideoTracks()[0];
          const audioTrack = localStream.getAudioTracks()[0];
          
          console.log('📊 Local tracks:', {
            video: !!videoTrack,
            audio: !!audioTrack,
            videoEnabled: videoTrack?.enabled,
            audioEnabled: audioTrack?.enabled,
            videoReadyState: videoTrack?.readyState
          });
          
          if (!videoTrack || !audioTrack) {
            console.error('❌ Missing required tracks!');
            return;
          }
          
          // Create Twilio LocalVideoTrack from existing track
          console.log('📹 Creating Twilio LocalVideoTrack...');
          const localVideoTrack = new Video.LocalVideoTrack(videoTrack);
          const localAudioTrack = new Video.LocalAudioTrack(audioTrack);
          
          // CRITICAL: Ensure audio track is enabled and unmuted
          localAudioTrack.enable();
          console.log('🔊 Local audio track explicitly enabled');
          console.log('📊 Local audio track state:', {
            isEnabled: localAudioTrack.isEnabled,
            isStopped: localAudioTrack.isStopped,
            mediaStreamTrack: localAudioTrack.mediaStreamTrack.enabled
          });
          
          console.log('✅ Twilio tracks created');
          
          // Connect to room with mobile-optimized settings
          console.log('🔗 Attempting Twilio room connection...');
          const room = await Video.connect(twilioToken, {
            name: twilioRoomName,
            tracks: [localVideoTrack, localAudioTrack],
            // 🔥 CRITICAL: Ensure audio tracks are subscribed
            automaticSubscription: true,
            // Track subscription options to ensure audio works
            trackSwitchOffMode: null, // Don't switch off tracks
            maxAudioBitrate: 16000,
            maxVideoBitrate: 500000,
            preferredVideoCodecs: ['VP8'],
            dominantSpeaker: true,
            networkQuality: {
              local: 1,
              remote: 1
            },
            // Add connection error handling
            logLevel: 'debug'
          });
          console.log('✅ Successfully connected to Twilio room');
          console.log('📊 Room info:', {
            name: room.name,
            sid: room.sid,
            localParticipant: room.localParticipant.identity,
            participants: room.participants.size,
            state: room.state
          });
          
          // 🔥 Add error listeners for connection issues
          room.on('reconnecting', error => {
            console.warn('⚠️ Twilio room reconnecting:', error.message);
          });
          
          room.on('reconnected', () => {
            console.log('✅ Twilio room reconnected');
          });
          
          room.on('disconnected', (room, error) => {
            if (error) {
              console.error('❌ Twilio room disconnected with error:', error.message);
            } else {
              console.log('👋 Twilio room disconnected normally');
            }
          });
          
          // Verify local tracks are published
          console.log('📤 Local tracks published:');
          room.localParticipant.tracks.forEach(publication => {
            console.log(`  - ${publication.kind}: ${publication.trackName} (${publication.isTrackEnabled ? 'enabled' : 'disabled'})`);
          });
          
          console.log('✅ Twilio connection complete');
          
          // Store room reference for cleanup
          twilioRoomRef.current = room;
          
          console.log('✅ Connected to Twilio room');
          
          // 🔥 CRITICAL DEBUG: Log connection order
          if (room.participants.size === 0) {
            console.log('🥇 I AM THE FIRST USER IN THE ROOM');
          } else {
            console.log('🥈 I AM THE SECOND USER IN THE ROOM (partner already here)');
            console.log('👥 Existing participants:', Array.from(room.participants.values()).map(p => p.identity));
          }
          
          // Check if partner is already in the room
          if (room.participants.size > 0) {
            console.log('✅ Partner is already in the room - ready to start timer');
            setPartnerConnected(true);
            // Start the backend timer when both users are connected
            await startBackendTimer();
          }
          
          // Handle existing participants - SIMPLIFIED AGGRESSIVE APPROACH
          room.participants.forEach(participant => {
            console.log('👤 EXISTING PARTICIPANT:', participant.identity);
            
            // 🔥 CRITICAL: Attach trackSubscribed listener FIRST before checking existing tracks
            participant.on('trackSubscribed', track => {
              console.log(`✅🎯 Track subscribed from existing participant: ${track.kind}`);
              
              if (track.kind === 'audio') {
                console.log('🔊🔊🔊 EXISTING PARTICIPANT AUDIO TRACK SUBSCRIBED - ATTACHING NOW');
                try {
                  // Remove any existing audio elements from this participant first
                  const existingAudio = document.querySelectorAll(`audio[data-participant="${participant.sid}"]`);
                  existingAudio.forEach(el => {
                    console.log('🗑️ Removing old audio element');
                    el.remove();
                  });
                  
                  const audioElement = track.attach();
                  audioElement.autoplay = true;
                  audioElement.muted = false;
                  audioElement.volume = 1.0;
                  audioElement.setAttribute('data-participant', participant.sid);
                  audioElement.style.display = 'none';
                  document.body.appendChild(audioElement);
                  
                  console.log('🔊 Audio element attached for existing participant:', {
                    autoplay: audioElement.autoplay,
                    muted: audioElement.muted,
                    volume: audioElement.volume
                  });
                  
                  audioElement.play().then(() => {
                    console.log('✅✅✅ EXISTING PARTICIPANT AUDIO PLAYING VIA TRACKSUBSCRIBED EVENT');
                    console.log('📊 Audio state:', {
                      paused: audioElement.paused,
                      currentTime: audioElement.currentTime,
                      readyState: audioElement.readyState
                    });
                  }).catch(err => {
                    console.warn('⚠️ Autoplay prevented:', err);
                    // Try to play on any user interaction
                    document.addEventListener('click', () => audioElement.play(), { once: true });
                  });
                } catch (err) {
                  console.error('❌ Error attaching audio from trackSubscribed:', err);
                }
              }
              
              if (track.kind === 'video' && !partnerStream) {
                console.log('📹 Existing participant video subscribed via event');
                handlePartnerVideoTrack(track);
              }
            });
            
            console.log('✅ trackSubscribed listener attached to existing participant');
            
            // SIMPLE: Just attach any video track immediately (only once)
            const attachAnyVideoTrack = () => {
              // Skip if we already have a working video
              if (partnerStream) {
                console.log('⚠️ Already have partnerStream, skipping attachAnyVideoTrack');
                return;
              }
              
              participant.tracks.forEach((publication) => {
                console.log(`📋 Processing publication: ${publication.kind}`);
                
                // Handle video tracks
                  if (publication.kind === 'video' || publication.trackName?.includes('camera')) {
                  console.log('📹 Found video publication, checking for track...');
                  console.log('📹 Publication isSubscribed:', publication.isSubscribed);
                  console.log('📹 Publication track:', publication.track);
                  
                  if (publication.track && !partnerStream) {
                    console.log('✅ TRACK EXISTS - ATTACHING NOW');
                    try {
                      const videoElement = publication.track.attach();
                      
                      // Safari iPad specific attributes
                      videoElement.setAttribute('autoplay', '');
                      videoElement.setAttribute('playsinline', '');
                      videoElement.setAttribute('webkit-playsinline', '');
                      videoElement.autoplay = true;
                      videoElement.playsInline = true;
                      videoElement.muted = false;
                      
                      // Simple styling
                      videoElement.style.width = '100%';
                      videoElement.style.height = '100%';
                      videoElement.style.objectFit = 'cover';
                      
                      if (partnerVideoRef.current) {
                        partnerVideoRef.current.innerHTML = '';
                        partnerVideoRef.current.appendChild(videoElement);
                        
                        // Apply Safari iOS/iPadOS workaround shim
                        shimVideoElement(publication.track, videoElement);
                        
                        setPartnerStream(publication.track);
                        console.log('✅✅✅ VIDEO ATTACHED SUCCESSFULLY');
                        
                        // Safari iPad: Multiple play attempts
                        videoElement.play().catch(err => {
                          console.error('❌ Play failed, retrying...', err);
                          setTimeout(() => videoElement.play(), 100);
                        });
                      }
                    } catch (err) {
                      console.error('❌ Attach error:', err);
                    }
                  } else if (!publication.isSubscribed && !partnerStream) {
                    // Track not yet subscribed - wait for subscription
                    console.log('⏳ Video publication found but not subscribed yet - waiting...');
                    const onSubscribed = (track) => {
                      console.log('✅ Track subscription complete for existing participant!');
                      if (track.kind === 'video' && !partnerStream) {
                        console.log('📹 Attaching newly subscribed existing participant video...');
                        try {
                          const videoElement = track.attach();
                          
                          videoElement.setAttribute('autoplay', '');
                          videoElement.setAttribute('playsinline', '');
                          videoElement.setAttribute('webkit-playsinline', '');
                          videoElement.autoplay = true;
                          videoElement.playsInline = true;
                          videoElement.muted = false;
                          
                          videoElement.style.width = '100%';
                          videoElement.style.height = '100%';
                          videoElement.style.objectFit = 'cover';
                          
                          if (partnerVideoRef.current) {
                            partnerVideoRef.current.innerHTML = '';
                            partnerVideoRef.current.appendChild(videoElement);
                            shimVideoElement(track, videoElement);
                            setPartnerStream(track);
                            console.log('✅✅✅ EXISTING PARTICIPANT VIDEO ATTACHED AFTER SUBSCRIPTION');
                            videoElement.play().catch(err => {
                              console.error('❌ Play failed, retrying...', err);
                              setTimeout(() => videoElement.play(), 100);
                            });
                          }
                        } catch (err) {
                          console.error('❌ Attach error for subscribed track:', err);
                        }
                      }
                      publication.off('subscribed', onSubscribed);
                    };
                    publication.on('subscribed', onSubscribed);
                  }
                }
                
                // Handle audio tracks
                if (publication.kind === 'audio') {
                  console.log('🔊 Found audio publication, checking for track...');
                  try {
                    console.log('🔊 Audio publication state:', {
                      isSubscribed: publication.isSubscribed,
                      isTrackEnabled: publication.isTrackEnabled !== undefined ? publication.isTrackEnabled : 'unknown',
                      hasTrack: !!publication.track,
                      trackName: publication.trackName
                    });
                  } catch (err) {
                    console.error('❌ Error logging audio publication state:', err);
                  }
                  
                  if (publication.track) {
                    console.log('✅ AUDIO TRACK EXISTS - ATTACHING NOW');
                    console.log('🔊 Existing audio track details:', {
                      isEnabled: publication.track.isEnabled,
                      isStopped: publication.track.isStopped
                    });
                    
                    try {
                      // Remove any existing audio elements from this participant first
                      const existingAudio = document.querySelectorAll(`audio[data-participant="${participant.sid}"]`);
                      existingAudio.forEach(el => el.remove());
                      
                      const audioElement = publication.track.attach();
                      audioElement.autoplay = true;
                      audioElement.muted = false; // CRITICAL: Ensure NOT muted
                      audioElement.volume = 1.0; // Full volume
                      audioElement.setAttribute('data-participant', participant.sid);
                      audioElement.style.display = 'none'; // Hide audio element (audio only)
                      document.body.appendChild(audioElement);
                      
                      // Explicitly play the audio
                      audioElement.play().then(() => {
                        console.log('✅✅✅ AUDIO ATTACHED AND PLAYING');
                      }).catch(err => {
                        console.warn('⚠️ Autoplay prevented, will play on user interaction:', err);
                      });
                    } catch (err) {
                      console.error('❌ Audio attach error:', err);
                    }
                  } else if (!publication.isSubscribed) {
                    // 🔥 CRITICAL FIX: Audio track not yet subscribed - wait for subscription
                    console.log('⏳ Audio publication found but not subscribed yet - setting up subscription handler...');
                    const onAudioSubscribed = (track) => {
                      console.log('✅🔊 Audio track subscription complete for existing participant!');
                      console.log('🔊 Subscribed audio track details:', {
                        isEnabled: track.isEnabled,
                        isStopped: track.isStopped,
                        mediaStreamEnabled: track.mediaStreamTrack.enabled
                      });
                      
                      try {
                        // Remove any existing audio elements from this participant first
                        const existingAudio = document.querySelectorAll(`audio[data-participant="${participant.sid}"]`);
                        existingAudio.forEach(el => el.remove());
                        
                        const audioElement = track.attach();
                        audioElement.autoplay = true;
                        audioElement.muted = false; // CRITICAL: Ensure NOT muted
                        audioElement.volume = 1.0; // Full volume
                        audioElement.setAttribute('data-participant', participant.sid);
                        audioElement.style.display = 'none'; // Hide audio element (audio only)
                        document.body.appendChild(audioElement);
                        
                        console.log('🔊 Audio element created after subscription:', {
                          autoplay: audioElement.autoplay,
                          muted: audioElement.muted,
                          volume: audioElement.volume
                        });
                        
                        // Explicitly play the audio
                        audioElement.play().then(() => {
                          console.log('✅✅✅ EXISTING PARTICIPANT AUDIO ATTACHED AND PLAYING AFTER SUBSCRIPTION');
                          console.log('📊 Audio playing state:', {
                            paused: audioElement.paused,
                            currentTime: audioElement.currentTime,
                            readyState: audioElement.readyState
                          });
                        }).catch(err => {
                          console.warn('⚠️ Autoplay prevented, will play on user interaction:', err);
                        });
                      } catch (err) {
                        console.error('❌ Audio attach error for subscribed track:', err);
                      }
                      publication.off('subscribed', onAudioSubscribed);
                    };
                    publication.on('subscribed', onAudioSubscribed);
                    console.log('✅ Audio subscription handler registered');
                  }
                }
              });
            };
            
            // Try once immediately for existing publications
            attachAnyVideoTrack();
          });
          
          // Handle new participants joining
          room.on('participantConnected', async (participant) => {
            console.log('👤 NEW participant connected:', participant.identity);
            console.log('👤 Participant SID:', participant.sid);
            setPartnerConnected(true);
            console.log('✅ Partner has joined - ready to start timer');
            // Start the backend timer when partner joins
            await startBackendTimer();
            
            // Listen for track subscriptions from the new participant
            participant.on('trackSubscribed', track => {
              console.log('📺 NEW participant trackSubscribed:', track.kind);
              
              if (track.kind === 'video' && !partnerStream) {
                console.log('🎥 New participant video track - attaching...');
                handlePartnerVideoTrack(track);
              }
              
              if (track.kind === 'audio') {
                console.log('🔊 New participant audio track - attaching...');
                console.log('🔊 Audio track details:', {
                  isEnabled: track.isEnabled,
                  isStopped: track.isStopped,
                  mediaStreamTrack: track.mediaStreamTrack.enabled
                });
                
                try {
                  // Remove any existing audio elements from this participant first
                  const existingAudio = document.querySelectorAll(`audio[data-participant="${participant.sid}"]`);
                  existingAudio.forEach(el => el.remove());
                  
                  const audioElement = track.attach();
                  audioElement.autoplay = true;
                  audioElement.muted = false; // CRITICAL: Ensure NOT muted
                  audioElement.volume = 1.0; // Full volume
                  audioElement.setAttribute('data-participant', participant.sid);
                  audioElement.style.display = 'none'; // Hide audio element (audio only)
                  document.body.appendChild(audioElement);
                  
                  console.log('🔊 Audio element created:', {
                    autoplay: audioElement.autoplay,
                    muted: audioElement.muted,
                    volume: audioElement.volume,
                    paused: audioElement.paused
                  });
                  
                  // Explicitly play the audio
                  audioElement.play().then(() => {
                    console.log('✅ NEW participant audio attached and playing');
                    console.log('📊 Playing state:', {
                      paused: audioElement.paused,
                      currentTime: audioElement.currentTime,
                      readyState: audioElement.readyState
                    });
                  }).catch(err => {
                    console.warn('⚠️ Autoplay prevented, will play on user interaction:', err);
                  });
                } catch (err) {
                  console.error('❌ Audio attach error:', err);
                }
              }
            });
            
            // Also check for already published tracks (in case subscription happens before we add listener)
            participant.tracks.forEach(publication => {
              if (publication.isSubscribed && publication.track) {
                console.log('📹 NEW participant has already subscribed track:', publication.kind);
                if (publication.kind === 'video' && !partnerStream) {
                  handlePartnerVideoTrack(publication.track);
                } else if (publication.kind === 'audio') {
                  console.log('🔊 Already subscribed audio track');
                  try {
                    // Remove any existing audio elements from this participant first
                    const existingAudio = document.querySelectorAll(`audio[data-participant="${participant.sid}"]`);
                    existingAudio.forEach(el => el.remove());
                    
                    const audioElement = publication.track.attach();
                    audioElement.autoplay = true;
                    audioElement.muted = false; // CRITICAL: Ensure NOT muted
                    audioElement.volume = 1.0; // Full volume
                    audioElement.setAttribute('data-participant', participant.sid);
                    audioElement.style.display = 'none'; // Hide audio element (audio only)
                    document.body.appendChild(audioElement);
                    
                    // Explicitly play the audio
                    audioElement.play().then(() => {
                      console.log('✅ NEW participant existing audio attached and playing');
                    }).catch(err => {
                      console.warn('⚠️ Autoplay prevented, will play on user interaction:', err);
                    });
                  } catch (err) {
                    console.error('❌ Audio attach error:', err);
                  }
                }
              } else if (!publication.isSubscribed) {
                console.log('⏳ NEW participant track not yet subscribed, waiting...');
              }
            });
          });
          
          // Handle participant disconnection
          room.on('participantDisconnected', participant => {
            console.log('👋 Participant disconnected:', participant.identity);
            setPartnerConnected(false);
            setPartnerStream(null);
            if (partnerVideoRef.current) {
              partnerVideoRef.current.innerHTML = '';
            }
          });
          
        } catch (error) {
          console.error('❌ Twilio connection error:', error);
          console.error('Error name:', error.name);
          console.error('Error message:', error.message);
          console.error('Error stack:', error.stack);
          alert(`Twilio connection failed on mobile: ${error.message}`);
        }
      }
    };
    
    const initTwilio = async () => {
      if (sessionReady && !twilioRoomName && (matchedUser?.id || partner?.id)) {
        const partnerId = matchedUser?.id || partner?.id;
        console.log('🎥 Auto-initializing Twilio for partner:', partnerId);
        await getTwilioToken(partnerId);
      }
    };
    
    initTwilio();
    connectToTwilioRoom();
  }, [sessionReady, matchedUser, partner, twilioRoomName, twilioToken, localStream]);
  
  // Handle partner video track
  // iPad Safari detection
  const isIPad = /iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Safari iOS/iPadOS video track workaround - prevent black video
  const shimVideoElement = (track, el) => {
    let wasInterrupted = false;

    const onPause = () => {
      console.log('🎬 Video paused - marking as interrupted');
      wasInterrupted = true;
    };

    const onPlay = () => {
      if (wasInterrupted) {
        console.log('🔄 Video resumed after interruption - re-attaching track');
        // Detach and re-attach for Safari iPad
        track.detach(el);
        track.attach(el);
        wasInterrupted = false;
      }
    };

    el.addEventListener('pause', onPause);
    el.addEventListener('play', onPlay);

    // Store listeners for cleanup
    el._twilioShimListeners = { onPause, onPlay };
  };

  const handlePartnerVideoTrack = (track) => {
    console.log('=== PARTNER VIDEO TRACK HANDLER START ===');
    console.log('📹 Track object:', track);
    console.log('📹 Track kind:', track?.kind);
    console.log('📹 Track mediaStream:', track?.mediaStream);
    console.log('📹 Track readyState:', track?.readyState);
    
    if (!partnerVideoRef.current) {
      console.error('❌ Partner video ref not available');
      return;
    }
    
    try {
      console.log('🔧 Setting up partner video...');
      setPartnerStream(track);

      // Detect Safari - improved detection
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      console.log('🧭 Browser Detection - Safari:', isSafari, '| iOS:', isIOS);

      // SAFARI FIX 2024-2025: Use track.mediaStream with srcObject instead of attach()
      // This resolves black screen issues on Safari/iOS
      // Reference: https://github.com/twilio/twilio-video.js/issues/1724
      
      if (isSafari || isIOS) {
        console.log('🧭 Safari/iOS detected - using mediaStream with srcObject method');
        
        // Create video element manually
        const videoElement = document.createElement('video');
        videoElement.setAttribute('autoplay', '');
        videoElement.setAttribute('playsinline', '');
        videoElement.setAttribute('webkit-playsinline', '');
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = false;
        
        // Apply styling
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        
        // CRITICAL: Set srcObject to track.mediaStream (not just attach())
        if (track.mediaStream) {
          videoElement.srcObject = track.mediaStream;
          console.log('✅ Safari: srcObject set to track.mediaStream');
        } else {
          // Fallback: use attach() if mediaStream not available
          console.warn('⚠️ Safari: mediaStream not available, falling back to attach()');
          const attachedElement = track.attach();
          videoElement.srcObject = attachedElement.srcObject;
        }
        
        // Clear container and append video element
        partnerVideoRef.current.innerHTML = '';
        partnerVideoRef.current.appendChild(videoElement);
        
        // Force play after ensuring video is ready
        const waitForVideoReady = () => {
          if (videoElement.readyState >= 2) {
            console.log('✅ Safari: Video ready (readyState:', videoElement.readyState, '), attempting play...');
            videoElement.play().then(() => {
              console.log('✅ Safari: Video playing successfully');
            }).catch(err => {
              console.warn('⚠️ Safari: Autoplay prevented, retrying...', err);
              // Retry after user interaction
              setTimeout(() => {
                videoElement.play().catch(e => console.error('❌ Safari play failed:', e));
              }, 500);
            });
          } else {
            console.log('⏳ Safari: Waiting for video data... (readyState:', videoElement.readyState, ')');
            setTimeout(waitForVideoReady, 100);
          }
        };
        
        setTimeout(waitForVideoReady, 200);
        
      } else {
        // Non-Safari: Use standard attach() method
        console.log('🧭 Non-Safari browser - using standard attach() method');
        
        const videoElement = track.attach();
        console.log('✅ Video element created via track.attach()');
        
        // Set video element attributes for cross-browser compatibility
        videoElement.setAttribute('autoplay', '');
        videoElement.setAttribute('playsinline', '');
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = false;
        
        // Apply styling
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.objectFit = 'cover';
        
        // Clear container and append video element
        partnerVideoRef.current.innerHTML = '';
        partnerVideoRef.current.appendChild(videoElement);
        
        // Standard play attempt
        setTimeout(() => {
          videoElement.play().catch(err => {
            console.warn('⚠️ Autoplay prevented:', err);
          });
        }, 100);
      }
      
      console.log('✅ Partner video attached successfully');
      
    } catch (error) {
      console.error('❌ Error attaching partner video:', error);
    }
  };

  // Session-specific joining functions
  const joinSpecificSession = async (sessionId) => {
    try {
      setSessionState('loading');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found for joining specific session');
        navigate('/login');
        return;
      }
      
      console.log('🎯 Joining specific session from notification:', sessionId);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/${sessionId}/join`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Join specific session response:', response.data);
      
      if (response.data.status === 'active') {
        // Session is active, set up the session
        setCurrentSession(response.data);
        setPartner(response.data.partner_info);
        setSessionState('in_session');
        setTimeLeft(response.data.time_remaining || 180);
        
        // Partner info is handled by setMatchedUser below
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Start countdown and session
        setSessionReady(true);
        
      } else if (response.data.status === 'waiting') {
        // Session is still waiting, show waiting message
        setSessionState('queuing');
        setCurrentSession(response.data);
        // Could show a waiting message to user
      }
    } catch (error) {
      console.error('❌ Error joining specific session:', error);
      setSessionState('error');
      // Clear URL parameters on error
      window.history.replaceState({}, document.title, window.location.pathname);
      // Fall back to normal session initialization
      await initializeSpeedDatingSession();
    }
  };

  // Start speed date with existing match - UNIVERSAL VERSION
  const startSpeedDateWithMatch = async (partnerId) => {
    console.log('🚀 UNIVERSAL startSpeedDateWithMatch called with partnerId:', partnerId);
    
    try {
      setLoadingProfile(false);
      
      // Set current profile data to prevent loading screen hang
      setCurrentProfileData({
        id: 'current-user',
        name: 'You',
        age: 28,
        interests: []
      });
      
      let partnerData = null;
      const token = localStorage.getItem('token');
      
      // UNIVERSAL FIX: Fetch match data from backend API using partner ID
      // This ensures we ALWAYS have correct, fresh data regardless of localStorage state
      try {
        console.log('🌐 Fetching match data from API for partnerId:', partnerId);
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user-matches/confirmed`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log('📦 API Response:', data);
          
          const matches = data.matches || [];
          console.log('📊 Total matches:', matches.length);
          console.log('🔍 Looking for partnerId:', partnerId);
          
          // Log all match user IDs to see what we have
          matches.forEach((m, index) => {
            console.log(`  Match ${index}: user.id = ${m.user?.id}, match_id = ${m.match_id}`);
          });
          
          // Find the specific match by user ID
          // The 'user' field contains the OTHER person (not current user)
          // So we match against their ID
          const matchData = matches.find(m => m.user.id === partnerId);
          
          console.log('🔍 Match lookup result:', matchData ? 'FOUND' : 'NOT FOUND');
          
          console.log('🎯 Found matchData:', matchData);
          
          if (matchData) {
            console.log('✅ Match found!');
            console.log('  user.id:', matchData.user.id);
            console.log('  user.name:', matchData.user.name);
            console.log('  match_id:', matchData.match_id);
            
            partnerData = {
              id: matchData.user.id,
              name: matchData.user.name,
              age: matchData.user.age,
              bio: matchData.user.bio,
              location: matchData.user.location,
              photo_url: matchData.user.photo_url,
              match_id: matchData.match_id
            };
            
            // UNIVERSAL FIX: Store match_id in React state for rating later
            console.log('💾 Setting currentMatchId to:', matchData.match_id);
            setCurrentMatchId(matchData.match_id);
            console.log('✅ Partner data prepared:', partnerData);
            
            // 🔥 QUEUE FIX: Check availability FIRST before creating session
            // BUT skip if already checked in previous component (to avoid duplicate check)
            const skipQueueCheck = location.state?.skipQueueCheck || false;
            
            if (!skipQueueCheck) {
              // Step 1: Request speed date (checks if partner is busy)
              console.log('🔍 Step 1: Checking if partner is available...');
              const requestResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/request`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  match_id: matchData.match_id
                })
              });
              
              if (!requestResponse.ok) {
                console.error('❌ Speed date request failed');
                throw new Error('Failed to request speed date');
              }
              
              const requestResult = await requestResponse.json();
              console.log('📊 Speed date request result:', requestResult);
              
              // Check if target has exhausted their sessions
              if (requestResult.target_limit_reached) {
                console.log('⚠️ Target user has exhausted free sessions');
                alert(`⚠️ Unable to Start Speed Date\n\n${requestResult.target_name} has used all their free speed dating sessions this month.\n\nThey need to upgrade to Premium to continue dating.`);
                navigate('/connections');
                return; // EXIT - do not proceed
              }
              
              // Check if we were queued (partner is busy)
              if (requestResult.queued) {
                console.log('⏸️ Partner is busy - you have been added to queue');
                console.log(`   Position: #${requestResult.position}`);
                
                // Show queue modal - user should NOT proceed to video
                alert(`${partnerData.name} is currently in another date.\n\nYou're #${requestResult.position} in queue!\n\nWe'll notify you when they're available.`);
                
                // Navigate back to matches/connections
                navigate('/connections');
                return; // EXIT - do not proceed to video session
              }
              
              // Partner is available! Proceed with session creation
              console.log('✅ Partner is available - proceeding to create session');
            } else {
              console.log('⏩ Skipping queue check (already verified in previous screen)');
            }
            
            // Step 2: Check for existing session first (for page refresh), then create if needed
            try {
              console.log('🔍 Checking for existing session...');
              const token = localStorage.getItem('token');
              
              // First, try to get existing session timer
              const timerResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/timer?match_id=${matchData.match_id}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              
              if (timerResponse.ok) {
                const timerData = await timerResponse.json();
                
                if (timerData.has_session) {
                  // 🔥 PRECISION FIX: Calculate remaining time RIGHT NOW to account for network/load delays
                  let remainingTime;
                  
                  // 🔍 DETAILED LOGGING - Check what backend sent
                  console.log('📊 BACKEND TIMER DATA:', JSON.stringify(timerData, null, 2));
                  
                  if (timerData.timer_started_at && timerData.timer_started) {
                    // Timer has started - calculate precise remaining time NOW
                    const timerStartedAt = new Date(timerData.timer_started_at);
                    const now = new Date();
                    const elapsedMs = now - timerStartedAt;
                    const elapsedSeconds = Math.floor(elapsedMs / 1000);
                    const sessionDuration = timerData.session_duration_seconds || 180;
                    remainingTime = Math.max(0, sessionDuration - elapsedSeconds);
                    
                    console.log(`✅ Found existing session - Precise calculation:`);
                    console.log(`   🎯 SESSION DURATION FROM BACKEND: ${sessionDuration}s`);
                    console.log(`   Timer started at: ${timerData.timer_started_at}`);
                    console.log(`   Current time: ${now.toISOString()}`);
                    console.log(`   Elapsed: ${elapsedSeconds}s / Duration: ${sessionDuration}s`);
                    console.log(`   ⏱️ REMAINING TIME CALCULATED: ${remainingTime}s`);
                    
                    if (sessionDuration !== 180) {
                      console.error(`❌ WRONG DURATION! Expected 180s, got ${sessionDuration}s`);
                    }
                  } else {
                    // Timer hasn't started yet - use full duration
                    remainingTime = timerData.session_duration_seconds || 180;
                    console.log(`✅ Found existing session - Timer not started yet`);
                    console.log(`   🎯 SESSION DURATION FROM BACKEND: ${remainingTime}s`);
                    
                    if (remainingTime !== 180) {
                      console.error(`❌ WRONG DURATION! Expected 180s, got ${remainingTime}s`);
                    }
                  }
                  
                  setTimeLeft(remainingTime);
                  setTimerInitialized(true);
                  
                  if (timerData.timer_expired) {
                    console.log('⚠️ Timer has already expired for this session');
                  }
                } else {
                  // No existing session - create a new one
                  console.log('🔒 No existing session found, creating new one...');
                  const sessionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/start?match_id=${matchData.match_id}`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  
                  if (sessionResponse.ok) {
                    const sessionData = await sessionResponse.json();
                    console.log('✅ New session created:', sessionData);
                    setTimeLeft(sessionData.session_duration_seconds || 180);
                    setTimerInitialized(true); // Mark timer as initialized
                  } else if (sessionResponse.status === 402) {
                    // Session limit reached for free users
                    const errorData = await sessionResponse.json();
                    console.warn('⚠️ Session limit reached:', errorData);
                    setSessionState('ready'); // Reset state so user isn't stuck in "joining session"
                    setShowUpgradeModal(true);
                    return; // Exit early
                  } else {
                    console.warn('⚠️ Failed to create session:', sessionResponse.status);
                    setTimeLeft(180);
                    setTimerInitialized(true);
                  }
                }
              } else {
                // Timer endpoint failed - try creating session anyway
                console.warn('⚠️ Timer check failed, attempting to create session...');
                const sessionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/start?match_id=${matchData.match_id}`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                
                if (sessionResponse.ok) {
                  const sessionData = await sessionResponse.json();
                  setTimeLeft(sessionData.session_duration_seconds || 180);
                  setTimerInitialized(true);
                } else {
                  setTimeLeft(180);
                  setTimerInitialized(true);
                }
              }
            } catch (sessionError) {
              console.error('⚠️ Session check/creation error:', sessionError);
              setTimeLeft(180);
              setTimerInitialized(true);
            }
          } else {
            console.error('❌ Match not found in API response!');
            console.error('  Searched for partnerId:', partnerId);
            console.error('  Available user IDs:', matches.map(m => m.user?.id));
          }
        } else {
          console.error('❌ API response not OK:', response.status, response.statusText);
        }
      } catch (apiError) {
        console.error('❌ API fetch failed:', apiError);
      }
      
      // NO FALLBACK: If we can't find the match, fail explicitly
      if (!partnerData) {
        console.error('❌ CRITICAL: Cannot find match data for partnerId:', partnerId);
        console.error('❌ API fetch failed OR match not found in response');
        throw new Error(`Match not found for partner ID: ${partnerId}. Please try again from the matches page.`);
      }
      
      console.log('✅ Partner data loaded successfully:', partnerData.name);
      
      // Set matched user for display (include match_id as fallback)
      setMatchedUserWithLog({
        id: partnerData.id,
        name: partnerData.name,
        age: partnerData.age,
        profession: 'Dating Match',
        location: partnerData.location,
        bio: partnerData.bio,
        photo_url: partnerData.photo_url,
        interests: ['Great Conversation', 'Speed Dating'],
        compatibility: Math.floor(Math.random() * 30) + 70,
        match_id: partnerData.match_id // Include match_id as emergency fallback
      });
      
      setPartner(partnerData);
      
      // Fetch personalized icebreakers for this session
      console.log('📋 Fetching personalized icebreakers...');
      await fetchPersonalizedIcebreakers();
      
      // Set session to ready - wait for timer initialization
      console.log('⚡ Waiting for timer initialization to complete...');
      
      // Small delay to ensure timer state update has propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('⏱️ Timer initialized:', timerInitialized);
      console.log('⏱️ CRITICAL: Final timeLeft value before session ready:', timeLeft);
      setSessionState('ready');
      setSessionReady(true);
      console.log('✅✅✅ Session ready! Timer will start from the correct value');
      
    } catch (error) {
      console.error('❌ Error in simplified startSpeedDateWithMatch:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Initialize speed dating session (demo mode only - partner mode is handled separately)
  const initializeSpeedDatingSession = async () => {
    // GUARD: This function should NEVER run when partner parameter exists
    const urlParams = new URLSearchParams(window.location.search);
    const partnerId = urlParams.get('partner');
    if (partnerId) {
      console.log('⛔ GUARD: initializeSpeedDatingSession called with partner parameter - SKIPPING');
      return; // EXIT immediately - partner mode uses startSpeedDateWithMatch instead
    }
    
    // This function should ONLY be called when there's NO partner parameter
    const isDemo = window.location.pathname === '/speed-dating-demo';
    
    if (isDemo) {
      console.log('🎭 Demo mode - setting up demo data (Alex Morgan)');
      
      setLoadingProfile(false);
      setProfileError(null);
      
      // Set up demo user profile (this prevents the loading screen)
      setCurrentProfileData({
        id: 'demo-current-user',
        name: 'Demo User',
        age: 26,
        bio: 'Ready for a great speed dating experience!',
        location: 'Demo City',
        photo_url: '/default-avatar.jpg',
        interests: ['Dating', 'Fun', 'Conversation']
      });
      
      // Set up demo match data
      console.log('🎭 Setting DEMO match data (Alex Morgan)');
      setMatchedUserWithLog({
        id: 'demo-match',
        name: 'Alex Morgan',
        age: 28,
        profession: 'Designer',
        location: 'San Francisco',
        bio: 'Love hiking, coffee, and great conversations!',
        photo_url: '/default-avatar.jpg',
        interests: ['Hiking', 'Coffee', 'Travel'],
        compatibility: 92
      });
      
      // Set up demo icebreakers
      setIcebreakers([
        "What's your favorite way to spend a weekend?",
        "If you could travel anywhere right now, where would you go?",
        "What's the best concert or live event you've attended?",
        "Do you prefer mountains or beaches for a vacation?",
        "What's your go-to comfort food?"
      ]);
      
      // Make sure we're not in loading state and session is ready
      setSessionState('ready');
      setTimerInitialized(true); // Demo mode starts with default 180s
      setSessionReady(true); // CRITICAL: Ensure session is ready for camera initialization
      
      console.log('🎭 Demo mode setup complete - sessionReady set to true with default timer (180s)');
      return;
    }

    // Regular authenticated mode (NOT demo, NOT partner mode)
    setLoadingProfile(true);
    setProfileError(null);

    try {
      // Check if there's a specific session to join from notification
      const urlParams = new URLSearchParams(window.location.search);
      const joinSessionId = urlParams.get('join');
      
      if (joinSessionId) {
        console.log('🎯 Joining specific session from notification:', joinSessionId);
        await joinSpecificSession(joinSessionId);
        return;
      }

      // UNIVERSAL FIX: No localStorage fallback needed
      // This path is only for demo/random match mode (not partner mode)
      // Only fetch data if not in demo mode
      const isStillDemo = window.location.pathname === '/speed-dating-demo';
      if (!isStillDemo) {
        console.log('🎲 Finding random match (no partner parameter)');
        await fetchCurrentUser();
        await findSpeedDateMatch();
        await fetchPersonalizedIcebreakers();
      }
      
    } catch (error) {
      console.error('Error initializing speed dating session:', error);
      setProfileError('Failed to start speed dating session. Please try again.');
    } finally {
      // Don't override states in demo mode since they're already set correctly
      const isStillDemo = window.location.pathname === '/speed-dating-demo';
      if (!isStillDemo) {
        setLoadingProfile(false);
        if (sessionState !== 'in_session') {
          setSessionState('ready');
        }
      }
    }
  };

  // Safety functions
  const handleBlockUser = () => {
    setShowBlockUserConfirm(false);
    setShowSafetyMenu(false);
    console.log(`Blocked user: ${matchedUser?.name || 'User'}`);
    // In real app, would call API
  };

  const handleReportUser = () => {
    if (!reportReason) return;
    setShowReportUser(false);
    setShowSafetyMenu(false);
    setReportReason('');
    setReportDetails('');
    console.log(`Reported user: ${matchedUser?.name || 'User'} for: ${reportReason}`);
    // In real app, would call API
  };

  const handleEndSession = async () => {
    console.log('🔴 === handleEndSession CALLED ===');
    console.log('🔴 This log confirms the function is executing');
    
    setShowEndSessionConfirm(false);
    setShowSafetyMenu(false);
    
    console.log('🛑 Ending session and cleaning up resources');
    
    // CRITICAL: Set sessionReady to false FIRST to prevent useEffect from re-triggering
    setSessionReady(false);
    setVideoReady(false);
    
    // CRITICAL: Stop local camera stream IMMEDIATELY
    if (localStream) {
      console.log('🛑 Stopping camera immediately');
      localStream.getTracks().forEach(track => {
        track.stop();
        console.log(`🛑 Stopped ${track.kind} track`);
      });
      setLocalStream(null);
      setVideoInitialized(false);
    }
    
    // Disconnect from Twilio and clean up video
    await cleanupTwilioConnection();
    
    // End speed dating session for queue system WITH RETRY LOGIC
    const token = localStorage.getItem('token');
    if (token) {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second between retries
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`🔄 Attempting to end session (attempt ${attempt}/${maxRetries})`);
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/end`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            console.log('✅ Speed date session ended for queue system');
            break; // Success - exit retry loop
          } else {
            console.warn(`⚠️ Session end returned status ${response.status} (attempt ${attempt})`);
            if (attempt === maxRetries) {
              console.error('❌ All retry attempts failed for session end');
            } else {
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          }
        } catch (error) {
          console.error(`⚠️ Failed to end session (attempt ${attempt}):`, error);
          if (attempt === maxRetries) {
            console.error('❌ All retry attempts exhausted. Session may be orphaned - passive cleanup will handle it.');
          } else {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }
      }
    }
    
    // End session on backend (don't wait for it)
    if (currentSession?.session_id) {
      if (token) {
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/sessions/${currentSession.session_id}/end`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(() => {
          console.log('✅ Session ended on backend');
        }).catch(error => {
          console.error('❌ Error ending session on backend:', error);
        });
      }
    }
    
    // Check if anyone is in queue for current user
    if (token) {
      try {
        const queueResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (queueResponse.ok) {
          const queueResult = await queueResponse.json();
          console.log('📋 Queue check result:', queueResult);
          
          // If there are people waiting in queue, show the queue modal
          if (queueResult.count > 0) {
            console.log('👥 People are waiting! Showing queue modal');
            setQueueData(queueResult);
            setShowRatingScreen(true); // Show rating first
            // Queue modal will show after rating is submitted
            return; // Don't navigate away yet
          }
        }
      } catch (error) {
        console.error('⚠️ Failed to check queue:', error);
      }
    }
    
    // Show rating screen instead of navigating immediately
    console.log('⭐ Showing rating screen');
    setShowRatingScreen(true);
  };
  
  const handleRating = async (rating) => {
    try {
      console.log('🎯 handleRating called');
      console.log('📊 Rating:', rating);
      
      // If it's a "like", show the memorable moment modal first
      if (rating === 'like') {
        console.log('❤️ Like detected - showing memorable moment modal');
        setPendingRating(rating);
        setShowMemorableMomentModal(true);
        return; // Don't submit yet - wait for memorable moment modal
      }
      
      // For "pass", submit immediately without memorable moment
      console.log('❌ Pass detected - submitting immediately');
      setUserRating(rating);
      setRatingSubmitted(true);
      
      const token = localStorage.getItem('token');
      
      console.log('🆔 currentMatchId state:', currentMatchId);
      console.log('👤 matchedUser state:', matchedUser);
      
      // UNIVERSAL FIX: Use match_id from React state instead of localStorage
      if (!currentMatchId) {
        console.error('❌ CRITICAL: No match_id in state - cannot submit rating');
        console.error('❌ This means API fetch failed or match_id was not stored');
        
        // Try to get match_id from matchedUser as emergency fallback
        const fallbackMatchId = matchedUser?.match_id;
        if (fallbackMatchId) {
          console.warn('⚠️ Using fallback match_id from matchedUser:', fallbackMatchId);
          // Continue with fallback
          return submitRatingToBackend(rating, fallbackMatchId, token, null);
        }
        
        alert('Unable to submit rating: Match data not found. Please try the speed date again.');
        setTimeout(() => {
          navigate('/matches');
        }, 2000);
        return;
      }
      
      console.log('✅ Using match_id from state:', currentMatchId);
      await submitRatingToBackend(rating, currentMatchId, token, null);
      
    } catch (error) {
      console.error('❌ Error in handleRating:', error);
      alert('Failed to submit rating. Please try again.');
      setTimeout(() => {
        navigate('/matches');
      }, 2000);
    }
  };
  
  const handleMemorableMomentSubmit = async () => {
    try {
      console.log('✨ Submitting memorable moment:', memorableMoment);
      setShowMemorableMomentModal(false);
      setUserRating(pendingRating);
      setRatingSubmitted(true);
      
      const token = localStorage.getItem('token');
      
      if (!currentMatchId) {
        const fallbackMatchId = matchedUser?.match_id;
        if (fallbackMatchId) {
          return submitRatingToBackend(pendingRating, fallbackMatchId, token, memorableMoment);
        }
        
        alert('Unable to submit rating: Match data not found. Please try the speed date again.');
        setTimeout(() => {
          navigate('/matches');
        }, 2000);
        return;
      }
      
      await submitRatingToBackend(pendingRating, currentMatchId, token, memorableMoment);
      
    } catch (error) {
      console.error('❌ Error submitting memorable moment:', error);
      alert('Failed to submit rating. Please try again.');
      setTimeout(() => {
        navigate('/matches');
      }, 2000);
    }
  };
  
  const handleSkipMemorableMoment = async () => {
    console.log('⏭️ Skipping memorable moment');
    setShowMemorableMomentModal(false);
    setUserRating(pendingRating);
    setRatingSubmitted(true);
    
    const token = localStorage.getItem('token');
    
    if (!currentMatchId) {
      const fallbackMatchId = matchedUser?.match_id;
      if (fallbackMatchId) {
        return submitRatingToBackend(pendingRating, fallbackMatchId, token, null);
      }
      
      alert('Unable to submit rating: Match data not found. Please try the speed date again.');
      setTimeout(() => {
        navigate('/matches');
      }, 2000);
      return;
    }
    
    await submitRatingToBackend(pendingRating, currentMatchId, token, null);
  };
  
  
  // Check queue for waiting users
  const checkQueue = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Checking queue for waiting users...');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Queue data:', data);
        console.log('📥 Queue count:', data.count);
        console.log('📥 Queue array length:', data.queue?.length);
        
        if (data.count > 0) {
          // People are waiting!
          console.log('👥 People are waiting in queue! Showing modal...');
          setQueueData(data);
          setShowQueueModal(true);
        } else {
          // No one waiting, go to matches
          console.log('✅ No one in queue, navigating to matches');
          navigate('/matches');
        }
      } else {
        console.error('❌ Failed to check queue, status:', response.status);
        navigate('/matches');
      }
    } catch (error) {
      console.error('❌ Error checking queue:', error);
      navigate('/matches');
    }
  };
  const submitRatingToBackend = async (rating, matchId, token, memorableMoment = null) => {
    try {
      console.log('📤 Submitting rating to backend...');
      console.log('  Match ID:', matchId);
      console.log('  Rating:', rating);
      console.log('  Memorable Moment:', memorableMoment);
      
      // Build request params
      const params = {
        match_id: matchId,
        rating: rating
      };
      
      // Add memorable moment if provided
      if (memorableMoment && memorableMoment.trim()) {
        params.memorable_moment = memorableMoment.trim();
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/rate`,
        null,
        {
          params: params,
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const result = response.data;
      console.log('✅ Rating submitted successfully!');
      console.log('📊 Full response:', JSON.stringify(result, null, 2));
      console.log('📊 Result.mutual_like:', result.mutual_like);
      console.log('📊 Result.requires_upgrade:', result.requires_upgrade);
      console.log('📊 Result.chat_unlocked:', result.chat_unlocked);
      console.log('📊 Result.user_tier:', result.user_tier);
      console.log('📊 Current userTier state:', userTier);
      
      // Handle result based on subscription and mutual like
      if (result.mutual_like) {
        console.log('✅ Mutual like detected!');
        
        if (result.requires_upgrade) {
          console.log('🆓🆓🆓 FREE USER DETECTED - SHOWING UPGRADE MODAL');
          console.log('🔔 Setting showUpgradeModal to TRUE in 2 seconds...');
          setTimeout(() => {
            console.log('🔔 NOW SETTING showUpgradeModal(true)');
            setShowUpgradeModal(true);
            console.log('🔔 showUpgradeModal state updated');
          }, 2000);
        } else if (result.chat_unlocked) {
          console.log('💎 Premium/VIP user - chat unlocked');
          // Check queue before navigating
          setTimeout(() => {
            checkQueue();
          }, 2000);
        } else {
          console.log('⚠️ Unexpected state - neither requires_upgrade nor chat_unlocked');
          setTimeout(() => {
            checkQueue();
          }, 2000);
        }
      } else {
        console.log('❌ No mutual like');
        // Still check queue even if no mutual like - someone might be waiting!
        setTimeout(() => {
          checkQueue();
        }, 2000);
      }
      
    } catch (error) {
      console.error('❌ Error submitting rating to backend:', error);
      console.error('❌ Error response:', error.response?.data);
      throw error; // Re-throw to be caught by outer try-catch
    }
  };

  // Loading screen while fetching user data or joining session
  if (loadingProfile || !currentProfileData || sessionState === 'loading') {
    let loadingMessage = "Setting up your speed dating session...";
    let loadingTitle = "Finding Your Perfect Match";
    
    if (sessionState === 'loading') {
      loadingMessage = "Connecting you to your speed dating partner...";
      loadingTitle = "Joining Session";
    } else if (sessionState === 'queuing') {
      loadingMessage = "Your partner is joining the session...";
      loadingTitle = "Almost Ready";
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-rose-900 flex items-center justify-center overflow-x-hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-rose-500 border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-2">{loadingTitle}</h2>
          <p className="text-gray-300 mb-4">
            {profileError ? profileError : loadingMessage}
          </p>
          {profileError && (
            <div className="space-x-4">
              <Button 
                onClick={() => navigate('/profile')}
                className="bg-rose-500 hover:bg-rose-600"
              >
                Complete Profile
              </Button>
              <Button 
                onClick={initializeSpeedDatingSession}
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
              >
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Countdown screen
  if (showCountdown) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="countdown-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-rose-900 backdrop-blur-xl z-50"
        >
          <div className="h-full flex flex-col items-center justify-center relative">
            <motion.div
              key={countdownNumber}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.2, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="mb-8 relative cursor-pointer hover:scale-105 transform"
              onClick={(e) => {
                e.preventDefault();
                console.log('🚀 COUNTDOWN BYPASSED - Starting session immediately!');
                setShowCountdown(false);
                setSessionReady(true);
                
                // Scroll to top when session starts
                setTimeout(() => {
                  window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                  });
                  console.log('📍 Scrolled to top after countdown bypass');
                }, 100);
              }}
              title="🚀 Click to start your speed date now!"
            >
              <div className={`text-8xl font-serif font-bold text-transparent bg-gradient-to-br mb-4 ${
                countdownNumber === 1 
                  ? 'from-red-400 via-rose-500 to-pink-600 animate-pulse' 
                  : countdownNumber <= 3 
                    ? 'from-orange-400 via-rose-500 to-red-600' 
                    : 'from-rose-400 via-pink-500 to-rose-600'
              } bg-clip-text`}>
                {countdownNumber}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6 text-center"
            >
              <div className="space-y-3">
                <h2 className="text-3xl font-bold text-white">
                  Can You Beat the <span style={{
                    color: '#E91E63',
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: 'italic',
                    fontWeight: '200'
                  }}>Shot Clock</span> to Find Your Match?!
                </h2>
                <p className="text-lg text-gray-300 max-w-md mx-auto">
                  Your conversation with <span className="text-rose-300 font-semibold">{matchedUser?.name || 'Your Match'}</span> starts soon
                </p>
              </div>
              
              {/* Partner Profile Card - Matching the original design */}
              <div className="max-w-md mx-auto">
                <div 
                  className="bg-gradient-to-br from-purple-600/40 to-pink-600/40 backdrop-blur-md rounded-3xl p-6 border border-white/20"
                  style={{
                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.6), rgba(236, 72, 153, 0.6))',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {matchedUser?.name?.charAt(0) || 'M'}
                    </div>
                    <div className="text-left">
                      <h3 className="text-white text-xl font-bold">{matchedUser?.name || 'Your Match'}, {matchedUser?.age || '25'}</h3>
                      <p className="text-gray-200">{matchedUser?.profession || 'Professional'}</p>
                      <p className="text-gray-300 text-sm">{matchedUser?.location || 'Nearby'}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium">Compatibility</span>
                      <span className="text-white font-bold">{matchedUser?.compatibility || 85}%</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${matchedUser?.compatibility || 85}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-3 text-center">Shared Interests</h4>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {['Art Galleries', 'Wine Tasting', 'Photography'].map((interest, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white/20 rounded-full text-white text-sm"
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom action buttons */}
              <div className="flex justify-center space-x-6 mt-8">
                <div className="flex items-center space-x-2 text-white">
                  <Camera className="w-5 h-5 text-green-400" />
                  <span>Camera Ready</span>
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <Mic className="w-5 h-5 text-green-400" />
                  <span>Audio Clear</span>
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <div className="w-5 h-5 bg-rose-400 rounded-full flex items-center justify-center">
                    ♥
                  </div>
                  <span>Let's Connect</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // If in video session mode, render VideoRoom instead of SpeedDatingDemo UI
  if (sessionState === 'video_session' && roomName && userIdentity) {
    return (
      <VideoRoom
        roomName={roomName}
        identity={userIdentity}
        onSessionEnd={() => {
          setSessionState('ended');
          setVideoReady(false);
          console.log('Video session ended');
        }}
        partnerInfo={partner}
      />
    );
  }

  // Rating screen after session ends
  if (showRatingScreen) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 overflow-x-hidden">
          <div className="max-w-lg w-full bg-white/10 backdrop-blur-xl rounded-3xl p-8 text-center">
            {!ratingSubmitted ? (
            <>
              <h2 className="text-3xl font-bold text-white mb-4">How was your speed date?</h2>
              <p className="text-white/80 mb-8">
                Did you enjoy your conversation with {matchedUser?.name}?
              </p>
              
              <div className="flex gap-4 justify-center mb-6">
                <button
                  onClick={() => handleRating('pass')}
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white rounded-2xl p-8 transition-all transform hover:scale-105"
                >
                  <X className="h-16 w-16 mx-auto mb-3" />
                  <span className="text-xl font-semibold">Pass</span>
                </button>
                
                <button
                  onClick={() => handleRating('like')}
                  className="flex-1 bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-2xl p-8 transition-all transform hover:scale-105"
                >
                  <Heart className="h-16 w-16 mx-auto mb-3 fill-white" />
                  <span className="text-xl font-semibold">Like</span>
                </button>
              </div>
            </>
          ) : (
            <>
              {userRating === 'like' ? (
                <>
                  <Heart className="h-20 w-20 text-rose-500 mx-auto mb-4 fill-rose-500" />
                  <h2 className="text-3xl font-bold text-white mb-4">Great choice!</h2>
                  <p className="text-white/80 mb-4">
                    We've recorded your interest in {matchedUser?.name}
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle className="h-20 w-20 text-white mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-white mb-4">Thanks for your feedback!</h2>
                  <p className="text-white/80">
                    Keep browsing to find your perfect match
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Upgrade Modal for Free Users - Renders on top of rating screen */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                It's a Match! 
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent"> Upgrade to Chat?</span>
              </h2>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                You both liked each other! Upgrade to Premium to unlock chat and continue the conversation.
              </p>
              
              <div className="bg-black/30 rounded-2xl p-6 mb-6 space-y-3">
                <h3 className="text-yellow-400 font-semibold text-lg mb-3">Premium Benefits:</h3>
                <div className="space-y-2 text-left">
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Unlock chat with your matches</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Video calls with matches</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Unlimited speed dates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">See who liked you</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    console.log('🎯 User clicked upgrade');
                    setShowUpgradeModal(false);
                    navigate('/subscription/plans');
                  }}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold py-4 text-lg rounded-xl shadow-lg transition"
                >
                  Upgrade Now - $9.99/month
                </button>
                
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/matches');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-xl transition"
                >
                  Maybe Later - Back to Matches
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Memorable Moment Modal - Elegant and Classy */}
      {showMemorableMomentModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[60] p-4 animate-fade-in">
          <div className="bg-gradient-to-br from-rose-50 via-white to-pink-50 rounded-3xl p-8 max-w-lg w-full shadow-2xl border-2 border-rose-200/50 relative overflow-hidden">
            {/* Elegant background pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-rose-200/20 to-pink-200/20 rounded-full blur-3xl -z-10"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-rose-200/20 rounded-full blur-3xl -z-10"></div>
            
            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-block p-3 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full mb-4">
                  <Heart className="h-10 w-10 text-rose-500 fill-rose-500" />
                </div>
                <h2 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  Capture the Moment
                </h2>
                <p className="text-gray-600 text-sm font-medium">
                  What stood out about {matchedUser?.name}?
                </p>
              </div>
              
              {/* Textarea */}
              <div className="mb-6">
                <textarea
                  value={memorableMoment}
                  onChange={(e) => {
                    if (e.target.value.length <= 250) {
                      setMemorableMoment(e.target.value);
                    }
                  }}
                  placeholder={`Their infectious laugh? Shared passion for travel? A thought-provoking conversation?`}
                  className="w-full h-32 px-4 py-3 bg-white border-2 border-rose-200 rounded-2xl focus:border-rose-400 focus:ring-4 focus:ring-rose-100 transition-all resize-none text-gray-700 placeholder-gray-400 text-base leading-relaxed"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                />
                <div className="flex justify-between items-center mt-2 px-1">
                  <p className="text-xs text-gray-500 italic">Optional, but helps you remember</p>
                  <span className={`text-sm font-medium ${memorableMoment.length >= 250 ? 'text-rose-500' : 'text-gray-400'}`}>
                    {memorableMoment.length}/250
                  </span>
                </div>
              </div>
              
              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleMemorableMomentSubmit}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02]"
                >
                  {memorableMoment.trim() ? 'Save & Continue ✨' : 'Skip for Now'}
                </button>
                
                {memorableMoment.trim() && (
                  <button
                    onClick={handleSkipMemorableMoment}
                    className="w-full bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-700 font-medium py-3 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all"
                  >
                    Skip
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Queue Notification Modal - Minimal Sophisticated */}
      {showQueueModal && queueData && (
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-auto" 
             style={{ 
               zIndex: 99999,
               background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95), rgba(30, 27, 75, 0.95))',
               backdropFilter: 'blur(16px)'
             }}>
          <div className="w-full max-w-lg">
            <div className="backdrop-blur-3xl bg-white/[0.08] rounded-[2rem] p-12 border border-white/10"
                 style={{
                   background: 'linear-gradient(145deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%)',
                   boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                 }}>
              
              <div className="text-center space-y-8">
                {/* Minimalist geometric accent */}
                <div className="inline-flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border-2 border-rose-400/30 relative">
                    <div className="absolute inset-2 rounded-full bg-gradient-to-br from-rose-500/20 to-pink-500/20"></div>
                  </div>
                </div>
                
                {/* Clean typography */}
                <div className="space-y-3">
                  <h2 className="text-3xl font-light text-white tracking-wide">
                    {queueData.count === 1 ? 'Connection Awaits' : `${queueData.total_waiting} Waiting`}
                  </h2>
                  <p className="text-white/60 text-base font-light">
                    Continue your journey or pause for a moment
                  </p>
                </div>
                
                {/* Elegant divider */}
                <div className="flex items-center justify-center gap-2">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-white/20"></div>
                  <div className="w-1.5 h-1.5 rounded-full bg-rose-400/40"></div>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-white/20"></div>
                </div>
                
                {/* Person card - minimal */}
                {queueData.queue && queueData.queue[0] && (
                  <div className="backdrop-blur-md bg-gradient-to-br from-white/[0.07] to-white/[0.03] rounded-2xl p-6 border border-white/[0.08]">
                    <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Next</p>
                    <p className="text-white text-xl font-light tracking-wide">{queueData.queue[0].requester_name}</p>
                  </div>
                )}
                
                {/* Refined buttons */}
                <div className="space-y-3 pt-4">
                  <button
                    onClick={async () => {
                      console.log('🎯 Start button clicked!');
                      try {
                        const token = localStorage.getItem('token');
                        const firstPerson = queueData.queue[0];
                        
                        console.log('🎯 Starting date with:', firstPerson);
                        
                        // CRITICAL FIX: Mark queue as 'accepted' instead of deleting
                        // This keeps notification visible for queued user but removes from target's queue
                        console.log('✅ Accepting queue request (not deleting)...');
                        console.log('📋 Queue entry details:', firstPerson);
                        try {
                          const acceptResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/queue/accept`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              queue_id: firstPerson.queue_id || firstPerson._id
                            })
                          });
                          
                          if (acceptResponse.ok) {
                            const result = await acceptResponse.json();
                            console.log('✅ Queue request accepted (status: accepted):', result);
                          } else if (acceptResponse.status === 402) {
                            // Queued user has no sessions left
                            const errorData = await acceptResponse.json();
                            console.warn('⚠️ Queued user has no sessions left:', errorData);
                            alert(errorData.detail?.message || 'This user has used all their free sessions for this month.');
                            setShowQueueModal(false);
                            return; // Don't navigate
                          } else {
                            console.warn('⚠️ Failed to accept queue request:', await acceptResponse.text());
                          }
                        } catch (queueError) {
                          console.error('❌ Error accepting queue request:', queueError);
                        }
                        
                        setShowQueueModal(false);
                        
                        // Force navigation by going to matches first, then to speed date
                        console.log('🔄 Navigating away first to force refresh...');
                        navigate('/matches');
                        
                        // Then navigate to new speed date after a small delay
                        setTimeout(() => {
                          console.log('🚀 Now navigating to speed date with partner:', firstPerson.requester_id);
                          navigate(`/speed-dating-demo?partner=${firstPerson.requester_id}`);
                        }, 100);
                      } catch (error) {
                        console.error('❌ Error:', error);
                      }
                    }}
                    className="w-full group relative overflow-hidden rounded-2xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.9), rgba(239, 68, 68, 0.9))',
                      boxShadow: '0 8px 24px rgba(236, 72, 153, 0.25)'
                    }}>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                    <span className="relative block py-4 text-white font-medium text-base tracking-wide">
                      Continue
                    </span>
                  </button>
                  
                  <button
                    onClick={async () => {
                      console.log('⏸️ Pause button clicked - Taking a break');
                      
                      try {
                        const token = localStorage.getItem('token');
                        
                        // Call the pause endpoint to notify queue and put items on hold
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speed-dating/session/pause`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        
                        if (response.ok) {
                          const result = await response.json();
                          console.log('✅ Session paused:', result);
                          console.log(`📬 ${result.queued_users_notified} users notified about break`);
                          console.log(`⏸️ ${result.queue_items_on_hold} queue items placed on hold`);
                        } else {
                          console.error('⚠️ Failed to pause session');
                        }
                      } catch (error) {
                        console.error('❌ Error pausing session:', error);
                      }
                      
                      setShowQueueModal(false);
                      navigate('/matches');
                    }}
                    className="w-full backdrop-blur-md bg-white/[0.05] hover:bg-white/[0.08] text-white/80 hover:text-white font-light py-4 rounded-2xl border border-white/10 hover:border-white/20 transition-all text-base tracking-wide">
                    Pause
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
    );
  }


  // Main session interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 overflow-x-hidden">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          onClick={() => window.location.href = '/'}
          variant="outline"
          className="text-white border-white/20 hover:bg-white/10"
        >
          <Home className="h-4 w-4 mr-2" />
          Back to Home
        </Button>
        
        {/* NBA Shot Clock Timer with Progressive Warnings (Option A) */}
        <div className="text-center">
          <div className={`relative transition-all duration-500 ${
            timeLeft <= 5 ? 'transform scale-125' : // Final 5 seconds: larger
            timeLeft <= 30 ? 'transform scale-110' : // Last 30 seconds: slightly larger
            timeLeft <= 60 ? 'transform scale-105' : // Last 60 seconds: slightly larger
            ''
          }`}>
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-600"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - (180 - timeLeft) / 180)}`}
                className={`transition-all duration-500 ${
                  timeLeft <= 10 ? 'text-red-500 animate-pulse' : // Last 10 seconds: red flashing
                  timeLeft <= 30 ? 'text-red-400' : // Last 30 seconds: red
                  timeLeft <= 60 ? 'text-orange-400' : // Last 60 seconds: orange
                  'text-blue-500' // Normal: blue
                } ${timeLeft <= 5 ? 'drop-shadow-lg' : ''}`}
                strokeLinecap="round"
              />
              
              {/* Additional urgent flash effect for final 10 seconds */}
              {timeLeft <= 10 && (
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - (180 - timeLeft) / 180)}`}
                  className="text-red-600 animate-ping opacity-75"
                  strokeLinecap="round"
                />
              )}
            </svg>
            
            <div className={`absolute inset-0 flex flex-col items-center justify-center ${
              timeLeft <= 10 ? 'animate-pulse' : ''
            }`}>
              <div className={`font-bold transition-all duration-300 ${
                timeLeft <= 5 ? 'text-3xl text-red-200' : // Final 5 seconds: larger, red
                timeLeft <= 30 ? 'text-2xl text-red-100' : // Last 30 seconds: red text
                timeLeft <= 60 ? 'text-2xl text-orange-100' : // Last 60 seconds: orange text
                'text-2xl text-white' // Normal: white
              }`}>
                {formatTime(timeLeft)}
              </div>
              <div className={`text-xs tracking-wide transition-all duration-300 ${
                timeLeft <= 30 ? 'text-red-200 font-bold' : // Last 30 seconds: red, bold
                timeLeft <= 60 ? 'text-orange-200 font-semibold' : // Last 60 seconds: orange, semibold
                'text-white' // Normal: white
              }`}>
                {timerPaused && !partnerConnected ? 'WAITING FOR PARTNER...' : timerPaused ? 'PAUSED' : 'SHOT CLOCK'}
              </div>
            </div>
            
            {/* Background glow for urgent final seconds */}
            {timeLeft <= 10 && (
              <div className="absolute inset-0 bg-red-500/20 rounded-full animate-pulse blur-xl"></div>
            )}
          </div>
        </div>

        {/* Controls with Enhanced Safety */}
        <div className="flex space-x-2" style={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          padding: '8px 12px',
          borderRadius: '12px',
          backdropFilter: 'blur(8px)'
        }}>
          <Button
            onClick={() => {
              if (!localStream) {
                // If no stream, try to initialize camera
                initializeCamera();
              } else {
                // Toggle camera track (this function handles state update)
                toggleCamera();
              }
              // REMOVED: setCameraEnabled(!cameraEnabled) - toggleCamera() already handles this
            }}
            variant={cameraEnabled ? "default" : "destructive"}
            size="sm"
            title={!localStream ? "Start Camera" : cameraEnabled ? "Turn Off Camera" : "Turn On Camera"}
            style={{
              opacity: 1,
              backgroundColor: cameraEnabled ? '#10b981' : '#ef4444',
              color: 'white',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            {cameraEnabled ? <Camera className="h-4 w-4" /> : <CameraOff className="h-4 w-4" />}
          </Button>
          <Button
            onClick={() => {
              toggleAudio();
            }}
            variant={audioEnabled ? "default" : "destructive"}
            size="sm"
            title={audioEnabled ? "Mute Your Microphone" : "Unmute Your Microphone"}
            style={{
              opacity: 1,
              backgroundColor: audioEnabled ? '#10b981' : '#ef4444',
              color: 'white',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            {audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          </Button>
          <Button
            onClick={() => {
              togglePartnerAudio();
            }}
            variant={partnerAudioEnabled ? "default" : "destructive"}
            size="sm"
            title={partnerAudioEnabled ? "Mute Partner's Audio" : "Unmute Partner's Audio"}
            style={{
              opacity: 1,
              backgroundColor: partnerAudioEnabled ? '#10b981' : '#ef4444',
              color: 'white',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            {partnerAudioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          
          {/* Emergency Exit Button (Option D) */}
          <Button
            onClick={() => {
              console.log('🚨 Emergency exit button clicked');
              setShowEndSessionConfirm(true);
              setSafetyStatus('alert');
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4"
            size="sm"
            title="Emergency Exit (or press ESC)"
          >
            <div className="flex items-center space-x-1">
              <span className="text-xs">EXIT</span>
            </div>
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Video Section */}
        <div className="lg:col-span-2">
          <Card className="bg-black/20 border border-white/20">
            <CardContent className="p-4">
                {/* Always use SpeedDatingDemo interface with integrated video */}
                {false ? null : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-[600px] sm:h-96">
                  {/* Your camera */}
                  <div className="relative bg-black rounded-lg overflow-hidden h-[280px] sm:h-full">
                    {/* Video element - ALWAYS visible */}
                    <video
                      ref={(el) => {
                        localVideoRef.current = el;
                        if (el && localStream && el.srcObject !== localStream) {
                          console.log('🔗 Video element ref callback - assigning stream');
                          el.srcObject = localStream;
                          
                          // Add mobile-friendly attributes
                          el.setAttribute('playsinline', 'true');
                          el.setAttribute('autoplay', 'true');
                          el.setAttribute('muted', 'true');
                          
                          el.play().catch(e => console.log('Play error in ref callback:', e.name));
                        }
                      }}
                      className="w-full h-full object-cover"
                      muted={true}
                      autoPlay={true}
                      playsInline={true}
                      controls={false}
                      style={{ 
                        minHeight: '100%',
                        minWidth: '100%'
                      }}
                      onLoadedMetadata={(e) => {
                        console.log('📺📺 Video metadata loaded!');
                        console.log('📺 Dimensions:', e.target.videoWidth, 'x', e.target.videoHeight);
                        console.log('📺 srcObject set:', !!e.target.srcObject);
                        console.log('📺 readyState:', e.target.readyState);
                      }}
                      onLoadedData={(e) => {
                        console.log('📺📺 Video data loaded!');
                      }}
                      onCanPlay={(e) => {
                        console.log('📺📺 Video can play!');
                      }}
                      onPlay={(e) => {
                        console.log('▶️▶️ Video started playing! ▶️▶️');
                        console.log('📺 Playing:', !e.target.paused);
                        console.log('📺 Current time:', e.target.currentTime);
                      }}
                      onPlaying={(e) => {
                        console.log('▶️▶️ Video is actively playing! ▶️▶️');
                      }}
                      onError={(e) => {
                        console.error('❌ Video element error:', e);
                        console.error('❌ Error details:', e.target.error);
                      }}
                    />
                    
                    {/* Success indicator when video is playing */}
                    {localStream && videoInitialized && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1 z-20">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span>LIVE</span>
                      </div>
                    )}
                    
                    {/* Placeholder overlay when no stream - covers video */}
                    {!localStream && sessionReady && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center z-10">
                        <div className="text-center">
                          <Camera className="w-16 h-16 text-white mb-4" />
                          <p className="text-white font-medium mb-4">Your Camera</p>
                          <button
                            onClick={() => {
                              console.log('🔘 Manual camera button clicked');
                              initializeCamera();
                            }}
                            className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition"
                          >
                            Start Camera
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Initial loading state */}
                    {!localStream && !sessionReady && (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center z-10">
                        <div className="text-center">
                          <Camera className="w-16 h-16 text-white mb-4 animate-pulse" />
                          <p className="text-white font-medium">Initializing...</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Partner video */}
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden h-[280px] sm:h-full">
                    {/* Placeholder when no partner video - BEHIND video container */}
                    {!partnerStream && (
                      <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
                        <div className="text-center p-4">
                          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-white text-2xl font-bold">{matchedUser?.name?.charAt(0) || 'M'}</span>
                          </div>
                          <h3 className="text-white font-bold text-2xl mb-1">{matchedUser?.name || 'Your Match'}</h3>
                          <p className="text-white/90 text-lg mb-1">{matchedUser?.age || 25} years old</p>
                          {matchedUser?.location && (
                            <p className="text-white/80 text-sm mb-3">{matchedUser.location}</p>
                          )}
                          <div className="mt-4 pt-4 border-t border-white/20">
                            <p className="text-white/60 text-sm">
                              {twilioRoomName ? 'Waiting for partner video track...' : 'Connecting to Twilio...'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Video container - Transparent background so placeholder shows through */}
                    <div ref={partnerVideoRef} className="w-full h-full relative" />
                    
                    {/* Partner name label */}
                    {partnerStream && (
                      <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                        {matchedUser?.name || 'Partner'}
                      </div>
                    )}
                    
                    {/* Unmute button for partner audio */}
                    {partnerStream && (
                      <button
                        onClick={() => {
                          const videoEl = partnerVideoRef.current?.querySelector('video');
                          if (videoEl) {
                            videoEl.muted = !videoEl.muted;
                            console.log('🔊 Partner audio muted:', videoEl.muted);
                          }
                        }}
                        className="absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
                        title="Toggle partner audio"
                      >
                        🔊
                      </button>
                    )}
                  </div>
                </div>
                )}
            </CardContent>
          </Card>
          
          {/* Always-Visible Safety Controls - Free standing without box */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  safetyStatus === 'safe' ? 'bg-green-400' : 
                  safetyStatus === 'caution' ? 'bg-yellow-400' : 'bg-red-400'
                } animate-pulse`}></div>
                <span className="text-white text-sm font-medium">Safety Controls</span>
              </div>
              
              <div className="flex space-x-2">
                <Button
                  onClick={() => {
                    setShowReportUser(true);
                    setSafetyStatus('alert');
                  }}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 text-xs shadow-lg"
                >
                  Report
                </Button>
                <Button
                  onClick={() => setShowBlockUserConfirm(true)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-xs shadow-lg"
                >
                  Block
                </Button>
                <Button
                  onClick={() => setShowEndSessionConfirm(true)}
                  size="sm"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 text-xs shadow-lg"
                >
                  End
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Icebreakers - Sidebar Pills with Enhanced Visibility */}
        <div className="space-y-6 hidden md:block">
          <div className="space-y-2">
            {/* Header Pill - Stronger Gradient & Larger Size */}
            <div className="bg-gradient-to-r from-rose-500/80 via-pink-500/70 to-purple-500/60 backdrop-blur-md border-2 border-rose-300/70 rounded-full px-6 py-3.5 flex items-center justify-between shadow-2xl transform hover:scale-105 transition-all duration-300">
              <span className="text-base font-extrabold text-white uppercase tracking-wider">Icebreakers</span>
              <button
                onClick={shuffleIcebreakers}
                className="text-white/90 hover:text-white transition-colors"
              >
                <Shuffle className="h-4.5 w-4.5" />
              </button>
            </div>
            
            {/* Question Pills - Enhanced Contrast */}
            {icebreakers.slice(currentIcebreaker, currentIcebreaker + 3).map((question, index) => {
              const actualIndex = currentIcebreaker + index;
              const isExpanded = expandedIcebreaker === actualIndex;
              
              return (
                <div
                  key={actualIndex}
                  onClick={() => setExpandedIcebreaker(isExpanded ? null : actualIndex)}
                  className="cursor-pointer"
                >
                  {isExpanded ? (
                    // Expanded view - More visible
                    <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-md border-2 border-white/40 rounded-2xl p-4 hover:from-white/25 hover:to-white/15 transition-all shadow-lg">
                      <p className="text-sm text-white leading-relaxed mb-3 font-medium">
                        {question}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/70">Click to collapse</span>
                        <MessageCircle className="h-3 w-3 text-rose-300" />
                      </div>
                    </div>
                  ) : (
                    // Collapsed pill - More prominent
                    <div className="bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-full px-4 py-2.5 hover:bg-white/30 hover:border-white/40 transition-all flex items-center space-x-2 shadow-md">
                      <MessageCircle className="h-3.5 w-3.5 text-rose-300 flex-shrink-0" />
                      <span className="text-xs text-white font-medium truncate flex-1">
                        {question}
                      </span>
                      <ChevronRight className="h-3 w-3 text-white/60 flex-shrink-0" />
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Next Pills Button - More Visible */}
            <button
              onClick={nextIcebreaker}
              className="w-full bg-white/15 backdrop-blur-md border-2 border-white/30 rounded-full px-4 py-2 hover:bg-white/25 hover:border-white/40 transition-all flex items-center justify-center space-x-2 text-white hover:text-white shadow-md"
            >
              <span className="text-xs font-medium">More questions</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>

          {/* Match Profile Card */}
          <Card className="bg-black/20 border border-white/20">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3 text-white text-xl font-bold">
                  {matchedUser?.name?.charAt(0) || 'M'}
                </div>
                <h3 className="text-white font-bold">{matchedUser?.name || 'Your Match'}</h3>
                <p className="text-gray-300 text-sm">{matchedUser?.profession || 'Professional'}</p>
                <p className="text-gray-400 text-xs">{matchedUser?.location || 'Nearby'}</p>
                
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-300 text-sm">Compatibility</span>
                    <span className="text-white font-bold">{matchedUser?.compatibility || 85}%</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${matchedUser?.compatibility || 85}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mobile Icebreaker Bubble - Enhanced visibility */}
      <div className="md:hidden fixed z-50" style={{bottom: '100px', right: '16px'}}>
        {!showMobileBubbleExpanded && (
          <div 
            className="cursor-pointer bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl p-3 shadow-xl hover:scale-105 transition-all duration-300 animate-pulse"
            onClick={() => setShowMobileBubbleExpanded(true)}
            title="Tap for icebreakers!"
          >
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-semibold">Icebreakers!</span>
            </div>
            <div className="text-xs text-white/90 text-center mt-1">
              Tap me!
            </div>
          </div>
        )}
        
        {showMobileBubbleExpanded && (
          <div className="w-72 space-y-2">
            {/* Header Pill with Close - More Prominent */}
            <div className="bg-gradient-to-r from-rose-500/30 to-pink-500/30 backdrop-blur-md border-2 border-rose-400/50 rounded-full px-4 py-2 flex items-center justify-between shadow-lg">
              <span className="text-xs font-bold text-white uppercase tracking-wide">Icebreakers</span>
              <button 
                onClick={() => setShowMobileBubbleExpanded(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            
            {/* Question Pills - Enhanced Contrast */}
            {icebreakers.slice(currentIcebreaker, currentIcebreaker + 3).map((question, index) => {
              const actualIndex = currentIcebreaker + index;
              const isExpanded = expandedIcebreaker === actualIndex;
              
              return (
                <div
                  key={actualIndex}
                  onClick={() => setExpandedIcebreaker(isExpanded ? null : actualIndex)}
                  className="cursor-pointer"
                >
                  {isExpanded ? (
                    // Expanded view - More visible
                    <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-md border-2 border-white/40 rounded-2xl p-4 hover:from-white/25 hover:to-white/15 transition-all shadow-lg">
                      <p className="text-sm text-white leading-relaxed mb-3 font-medium">
                        {question}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/70">Tap to collapse</span>
                        <MessageCircle className="h-3 w-3 text-rose-300" />
                      </div>
                    </div>
                  ) : (
                    // Collapsed pill - More prominent
                    <div className="bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-full px-4 py-2.5 hover:bg-white/30 hover:border-white/40 transition-all flex items-center space-x-2 shadow-md">
                      <MessageCircle className="h-3.5 w-3.5 text-rose-300 flex-shrink-0" />
                      <span className="text-xs text-white font-medium truncate flex-1">
                        {question}
                      </span>
                      <ChevronRight className="h-3 w-3 text-white/60 flex-shrink-0" />
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Actions - More Visible */}
            <div className="flex space-x-2">
              <button
                onClick={shuffleIcebreakers}
                className="flex-1 bg-white/15 backdrop-blur-md border-2 border-white/30 rounded-full px-3 py-2 hover:bg-white/25 hover:border-white/40 transition-all text-xs text-white font-medium flex items-center justify-center space-x-1.5 shadow-md"
              >
                <Shuffle className="h-3 w-3" />
                <span>Mix</span>
              </button>
              <button
                onClick={nextIcebreaker}
                className="flex-1 bg-white/15 backdrop-blur-md border-2 border-white/30 rounded-full px-3 py-2 hover:bg-white/25 hover:border-white/40 transition-all text-xs text-white font-medium flex items-center justify-center space-x-1.5 shadow-md"
              >
                <span>Next</span>
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>


      {/* End Session Confirmation Modal */}
      {showEndSessionConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">End Session?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to end this speed dating session?
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowEndSessionConfirm(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                >
                  Continue Dating
                </Button>
                <Button
                  onClick={handleEndSession}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                >
                  End Session
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block User Confirmation Modal */}
      {showBlockUserConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center">
              <UserX className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Block {matchedUser?.name || 'User'}?</h3>
              <p className="text-gray-600 mb-6">
                Blocking will prevent this user from appearing in your future matches. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowBlockUserConfirm(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBlockUser}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                >
                  Block User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report User Modal */}
      {showReportUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <Flag className="w-6 h-6 mr-2 text-orange-500" />
                Report {matchedUser?.name || 'User'}
              </h3>
              <button
                onClick={() => setShowReportUser(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Report *
                </label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="inappropriate_behavior">Inappropriate behavior</option>
                  <option value="harassment">Harassment or bullying</option>
                  <option value="fake_profile">Fake profile</option>
                  <option value="spam">Spam</option>
                  <option value="underage">Appears underage</option>
                  <option value="threats">Threats or violence</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Provide any additional context..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowReportUser(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReportUser}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={!reportReason}
                >
                  Submit Report
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Safety Tips Modal */}
      {showSafetyTips && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <Shield className="w-6 h-6 mr-2 text-blue-500" />
                Safety Tips
              </h3>
              <button
                onClick={() => setShowSafetyTips(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">During Your Session:</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Keep conversations light and fun</li>
                  <li>• Don't share personal contact information</li>
                  <li>• Trust your instincts about people</li>
                  <li>• Use our platform's messaging system first</li>
                </ul>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">Emergency Situations:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Use the emergency exit button for immediate safety</li>
                  <li>• Contact local emergency services if threatened</li>
                  <li>• Report serious incidents to our support team</li>
                </ul>
              </div>
              
              <div className="text-center">
                <Button
                  onClick={() => setShowSafetyTips(false)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8"
                >
                  Got It
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Safety Onboarding Modal - Professional & Sleek Design */}
      {showSafetyOnboarding && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-white/10">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-blue-500/30">
                <Shield className="w-10 h-10 text-blue-400" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-3">Your Safety is Our Priority</h2>
              <p className="text-gray-300 text-sm">We've implemented comprehensive safety features for your protection</p>
            </div>
            
            <div className="space-y-3 mb-8">
              <div className="flex items-start space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Safety Controls</h4>
                  <p className="text-sm text-gray-400">Report, block, or end your session at any time using the controls above</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Quick Exit</h4>
                  <p className="text-sm text-gray-400">Press <kbd className="bg-white/10 border border-white/20 px-2 py-1 rounded text-xs text-white">ESC</kbd> for immediate exit</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Periodic Check-ins</h4>
                  <p className="text-sm text-gray-400">Optional safety reminders during your conversation</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  console.log('🎯 "Got it, don\'t remind me" button clicked');
                  setShowSafetyOnboarding(false);
                  setSafetyOnboardingDismissed(true);
                  setSafetyRemindersEnabled(false);
                  
                  // Only start timer if partner has already joined
                  if (partnerConnected) {
                    setTimerPaused(false);
                    setSessionStartTime(Date.now());
                    console.log('▶️ Timer started - partner already connected (reminders disabled)');
                  } else {
                    console.log('⏳ Waiting for partner to join before starting timer (reminders disabled)');
                  }
                  console.log('🎯 Timer state - timerPaused:', !partnerConnected, 'partnerConnected:', partnerConnected);
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm transition-all"
              >
                Skip Reminders
              </Button>
              <Button
                onClick={() => {
                  console.log('🎯 "Thanks, I feel safe!" button clicked');
                  setShowSafetyOnboarding(false);
                  setSafetyOnboardingDismissed(true);
                  
                  // Only start timer if partner has already joined
                  if (partnerConnected) {
                    setTimerPaused(false);
                    setSessionStartTime(Date.now());
                    console.log('▶️ Timer started - partner already connected (reminders enabled)');
                  } else {
                    console.log('⏳ Waiting for partner to join before starting timer (reminders enabled)');
                  }
                  console.log('🎯 Timer state - timerPaused:', !partnerConnected, 'partnerConnected:', partnerConnected);
                }}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20 transition-all"
              >
                Continue with Reminders
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Periodic Safety Reminder Toast (Option D) */}
      {showSafetyReminder && (
        <div className="fixed top-20 right-4 z-40 max-w-sm">
          <Card className="bg-blue-600 border border-blue-400 shadow-xl">
            <CardContent className="p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  💙
                </div>
                <div>
                  <h4 className="text-white font-semibold text-sm mb-1">Safety Check-in</h4>
                  <p className="text-white/90 text-xs mb-3">
                    {sessionStartTime && (Date.now() - sessionStartTime) / 1000 >= 90 
                      ? "How are you feeling about this conversation?" 
                      : "Remember: You can report inappropriate behavior anytime"}
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => setShowSafetyReminder(false)}
                      size="sm"
                      className="bg-white/20 hover:bg-white/30 text-white text-xs px-2 py-1"
                    >
                      I'm good ✓
                    </Button>
                    <Button
                      onClick={() => {
                        setShowSafetyReminder(false);
                        setShowSafetyMenu(true);
                      }}
                      size="sm"
                      className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1"
                    >
                      Need help
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Upgrade Modal - Appears when timer runs out for free users */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Time's Up! 
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent"> Upgrade?</span>
              </h2>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                You liked each other! Upgrade to Premium to unlock chat and video calls with your matches.
              </p>
              
              <div className="bg-black/30 rounded-2xl p-6 mb-6 space-y-3">
                <h3 className="text-yellow-400 font-semibold text-lg mb-3">Premium Benefits:</h3>
                <div className="space-y-2 text-left">
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Unlock chat with your matches</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Video calls with matches</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Unlimited speed dates</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Advanced icebreaker questions</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400">✓</span>
                    <span className="text-white text-sm">Priority matching</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    console.log('🎯 User clicked upgrade');
                    setShowUpgradeModal(false);
                    navigate('/subscription/plans');
                  }}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold py-4 text-lg rounded-xl shadow-lg"
                >
                  Upgrade Now - $9.99/month
                </Button>
                
                <Button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/matches');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-xl"
                >
                  Maybe Later - Back to Matches
                </Button>
                
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full text-gray-400 hover:text-gray-300 text-sm py-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIP Session Complete Modal - For premium/VIP users */}
      {showVipSessionComplete && (() => {
        console.log('🎯 VIP Session Complete Modal is rendering!');
        return true;
      })() && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-rose-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/20">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Great Session! 
                <span className="bg-gradient-to-r from-yellow-400 to-rose-500 bg-clip-text text-transparent"> What's Next?</span>
              </h2>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                As a VIP member, you have unlimited access to all features. Ready for your next connection?
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    console.log('🎯 VIP user accessing coaching');
                    setShowVipSessionComplete(false);
                    navigate('/coaching/dashboard');
                  }}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-bold py-4 text-lg rounded-xl shadow-lg"
                >
                  💪 VIP Coaching Dashboard
                </Button>
                
                <Button
                  onClick={() => {
                    console.log('🎯 VIP user viewing account');
                    setShowVipSessionComplete(false);
                    navigate('/account');
                  }}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-bold py-3 rounded-xl"
                >
                  📊 View Account Dashboard
                </Button>
                
                <button
                  onClick={() => {
                    setShowVipSessionComplete(false);
                    navigate('/speed-dating');
                  }}
                  className="w-full text-gray-400 hover:text-gray-300 text-sm py-2"
                >
                  Return to Speed Dating Lobby
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeedDatingDemo;